// Client for the streaming chat endpoint (POST /api/chat).
//
// The server is a Lambda Function URL in RESPONSE_STREAM mode (see the
// terraform repo's modules/lambda/chat.tf) rather than the API Gateway the
// other endpoints use, because API Gateway buffers whole responses and would
// defeat streaming. CloudFront proxies /api/chat to it; local dev needs
// CHAT_PROXY set (see webpack.config.js).
//
// Wire format is SSE, one JSON object per `data:` frame:
//   {delta}  text to append   {nav}  suggested pages   {done}  finished
//   {error}  fatal — nothing more will arrive
//
// This module also owns thread persistence, which now has a second kind of
// message in it: the RECEIPT an agent operation posts when it finishes. See
// packRunMessage() near the bottom for what a receipt carries and what it
// deliberately does not.
import { getIdToken } from './cognito.js'
import { keyOf } from './companyMeta.js'
import {
  listRemote,
  getRemote,
  putRemote,
  putRemoteMany,
  deleteRemote,
  getActingAs,
  msOf,
  MAX_BODY_CHARS,
} from './chatCloud.js'
import { sessionEmail } from './userScope.js'
// Who a receipt card is addressed to — the pure reader behind the
// `forRecipient` check in patchReceiptLead (a draft written for one person
// must not land under another's name).
import { currentRecipientOf } from './leadContacts.js'
import { greetingFor } from './greeting.js'
import { parsePackedContacts } from './leadSearch.js'
import {
  buildOutreachEmail,
  buildFollowupEmail,
  humanizeDraft,
  stripInlineCitations,
  templateExec,
} from './outreachDraft.js'
import { isEmailTouch } from './cadence.js'

// The deployed chat Function URL (CORS-open), same role as data.jsx's
// API_FALLBACK_BASE: when the same-origin /api/chat proxy isn't real — plain
// localhost without CHAT_PROXY — the client falls through to this directly, so
// dev works out of the box against the live backend.
const CHAT_FALLBACK_BASE =
  'https://yqcel4kq6a33x5ktbsrz7b6j5y0vfhfv.lambda-url.us-east-1.on.aws/'

// The STREAMING server is stateless: every turn posts the history it wants the
// model to see, which keeps the chat lambda free of any store. The THREADS
// themselves are no longer browser-only — they live in DynamoDB keyed on the
// caller's Cognito sub, and this key is now the local CACHE in front of that
// (see the sync section at the bottom of this file).
const STORAGE_BASE = 'revailosales.chat'

/**
 * Stream one assistant turn.
 *
 * `onDelta` is called with each text fragment as it arrives — that is what
 * makes the answer type out rather than appear at once. Resolves with the full
 * text and any nav links once the stream closes.
 *
 * Pass an AbortSignal to stop generation; aborting resolves with whatever had
 * already streamed (`stopped: true`) rather than throwing, because a partial
 * answer the user chose to cut short is a normal outcome, not an error.
 */
export async function streamChat({ messages, context, onDelta, signal }) {
  const token = await getIdToken()
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  const body = JSON.stringify({ messages, context })
  const post = (url) => fetch(url, { method: 'POST', headers, body, signal })

  let res
  try {
    res = await post('/api/chat')
    // The dev server's history fallback answers unknown paths with index.html
    // and a 200, so a missing proxy looks like success until you check the
    // type. When that happens, go straight to the deployed Function URL.
    const ct = res.headers.get('content-type') || ''
    if ((!res.ok || !ct.includes('text/event-stream')) && CHAT_FALLBACK_BASE) {
      res = await post(CHAT_FALLBACK_BASE)
    }
  } catch (err) {
    if (err?.name === 'AbortError') return { text: '', nav: [], stopped: true }
    throw new Error('Chat is unreachable — check your connection')
  }

  const contentType = res.headers.get('content-type') || ''
  if (!res.ok || !contentType.includes('text/event-stream')) {
    throw new Error(
      res.status === 404 || contentType.includes('text/html')
        ? 'Chat backend is unreachable — check the deployed stack'
        : `Chat failed (${res.status})`,
    )
  }
  if (!res.body) throw new Error('This browser cannot stream responses')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let text = ''
  let nav = []

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // Frames are \n\n-delimited; keep the trailing partial for the next read
      // so a fragment split mid-JSON is never parsed.
      const parts = buffer.split('\n\n')
      buffer = parts.pop() ?? ''

      for (const part of parts) {
        const line = part.trim()
        if (!line.startsWith('data:')) continue
        let frame
        try {
          frame = JSON.parse(line.slice(5).trim())
        } catch {
          continue
        }
        if (frame.error) throw new Error(frame.error)
        if (frame.delta) {
          text += frame.delta
          onDelta?.(frame.delta)
        }
        if (Array.isArray(frame.nav)) nav = frame.nav
      }
    }
  } catch (err) {
    if (err?.name === 'AbortError' || signal?.aborted) return { text, nav, stopped: true }
    throw err
  } finally {
    // Releasing matters on abort: it tells the browser to close the connection
    // instead of leaving the lambda streaming into nothing.
    //
    // cancel() returns a promise that REJECTS on an already-aborted body, so it
    // needs .catch() — a synchronous try/catch here would let that rejection
    // escape as an unhandled one and trip React's error overlay.
    reader.cancel().catch(() => {
      /* already closed */
    })
  }

  return { text, nav, stopped: false }
}

// ── conversation persistence ───────────────────────────────────────────────
// Scoped per signed-in user, same pattern as the other local stores.
function key(scope) {
  return `${STORAGE_BASE}.${scope || 'anon'}`
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function loadConversations(scope) {
  try {
    const raw = JSON.parse(localStorage.getItem(key(scope)) || '[]')
    const list = Array.isArray(raw) ? raw : []
    // One-time id migration: a conversation's id is its URL now (/chat/<id>),
    // and the URL should read as a UUID — so any thread still carrying a
    // legacy `c_…` id is re-keyed on first load and the store rewritten. The
    // id is opaque everywhere it is read (messages don't reference it), so
    // re-keying costs nothing except old bookmarks, which bounce to /chat.
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function' &&
      list.some((c) => c && typeof c.id === 'string' && !UUID_RE.test(c.id))
    ) {
      const migrated = list.map((c) =>
        c && typeof c.id === 'string' && !UUID_RE.test(c.id) ? { ...c, id: crypto.randomUUID() } : c,
      )
      try {
        localStorage.setItem(key(scope), JSON.stringify(migrated))
      } catch {
        /* a full store just means the rename waits for the next load */
      }
      return migrated
    }
    return list
  } catch {
    return []
  }
}

// How much JSON one user's threads may occupy. Threads used to be nothing but
// text, so a thread cap was budget enough; a run receipt carries a graph and a
// per-lead trace, so the cap is now on SIZE as well as on count. 1.5M UTF-16
// characters is ~3MB of the ~5MB every browser gives an origin, with room for
// the other stores (daily tasks, the activity ledger, the workspace caches)
// that share it. Raised from 600k on 2026-10-12: at fifty threads the old
// budget was spent by the time a demo ran, so every new receipt was shed the
// moment it was saved and rendered "shows the first 0" over fifty drafted
// emails. The budget is the second line of defence now - a shed receipt that
// names its server run is refilled from the run (lib/receiptRefill.js).
const STORE_BUDGET = 1_500_000

/**
 * Drop the per-lead trace from one receipt, and say how many leads' provenance
 * that cost. `traceOmitted` is the field the run itself uses to report what its
 * own snapshot shed, so the flow view already reads it and already says so —
 * shedding here just makes the number bigger, never quieter.
 */
function shedTrace(msg) {
  const trace = msg?.result?.trace
  if (!trace) return msg
  const shed = Object.keys(trace).length
  return {
    ...msg,
    traceShed: (msg.traceShed || 0) + shed,
    result: {
      ...msg.result,
      trace: null,
      traceOmitted: (Number(msg.result.traceOmitted) || 0) + shed,
    },
  }
}

/**
 * Drop the stored grounding from one message.
 *
 * `debug` exists for the Markdown export (lib/chatExport.js) — valuable to a
 * reviewer, worth nothing to the thread on screen. So it is the FIRST thing
 * shed when the browser's quota bites: an export that says the grounding was
 * dropped is a small loss, a thread that fails to save is a real one. What
 * remains records that it happened, so the export never implies the answer had
 * no grounding when it simply was not kept.
 */
function shedDebug(msg) {
  if (!msg || msg.role !== 'assistant' || !msg.debug) return msg
  return {
    ...msg,
    debug: { _shed: 'the grounding for this answer was dropped to fit this browser’s storage' },
  }
}

/**
 * Drop a receipt's whole run payload. The receipt keeps its text — what it
 * built, and how many leads — so the thread still reads correctly; it simply
 * can no longer open the flow, and says so (`runShed`). A wrong receipt would
 * be worse than one that admits it lost its evidence.
 */
function shedRun(msg) {
  if (msg.runShed) return msg
  return {
    ...msg,
    passes: [],
    result: null,
    leads: [],
    // The worklist lanes are evidence too — a shed receipt keeps its text (the
    // counts in the title stay honest) but loses the rows, exactly like leads.
    // `inboxUnread` survives: it is one boolean, and "the inbox could not be
    // read" stays true of the run however much of its payload was shed.
    calls: [],
    waiting: [],
    manualReply: [],
    inactiveUsers: [],
    expiredSubscriptions: [],
    trialUsers: [],
    // The task cards too (2026-10-12). They were left out when Daily Tasks
    // was recut, so a task receipt could never be shed - its sixty-odd emails
    // sat in the store for good while every outreach and Follow-Up receipt
    // around it was emptied to make room. `taskCount` keeps the title honest.
    tasks: [],
    runShed: true,
  }
}

/**
 * Serialize the threads, shedding in a fixed order until they fit: oldest
 * thread first and oldest message first, traces before whole payloads, whole
 * payloads before entire threads. The newest thread is shed last because it is
 * the one on screen.
 */
export function fit(conversations, budget) {
  let json = JSON.stringify(conversations)
  if (json.length <= budget) return json

  // A copy we own: `conversations` is React state and must not be mutated.
  const list = conversations.map((c) => ({ ...c, messages: (c.messages || []).slice() }))
  const targets = []
  for (let c = list.length - 1; c >= 0; c -= 1) {
    for (let m = 0; m < list[c].messages.length; m += 1) {
      if (list[c].messages[m]?.role === 'run') targets.push([c, m])
    }
  }
  // The grounding first, and over EVERY message — `targets` below is receipts
  // only, and the grounding hangs off assistant turns.
  for (let c = list.length - 1; c >= 0 && json.length > budget; c -= 1) {
    for (let m = 0; m < list[c].messages.length && json.length > budget; m += 1) {
      const next = shedDebug(list[c].messages[m])
      if (next === list[c].messages[m]) continue
      list[c].messages[m] = next
      json = JSON.stringify(list)
    }
  }
  for (const step of [shedTrace, shedRun]) {
    for (const [c, m] of targets) {
      if (json.length <= budget) break
      const next = step(list[c].messages[m])
      if (next === list[c].messages[m]) continue
      list[c].messages[m] = next
      json = JSON.stringify(list)
    }
  }
  // Last resort: whole threads, oldest first. Never the last one standing.
  while (json.length > budget && list.length > 1) {
    list.pop()
    json = JSON.stringify(list)
  }
  return json
}

// How many threads this BROWSER'S CACHE keeps (localStorage, under
// STORE_BUDGET). The cloud is not capped by it — nothing here ever deletes a
// stored row for being old — and neither is the History list on screen: that
// is VIEW_LIMIT. Until 2026-10-13 this one number capped both, so History sat
// at 50 however many chats the account held ("history number keeps getting
// stuck at 50"); the 51st and older were saved, just never shown.
export const CONVO_LIMIT = 50

/**
 * Shed ONE conversation until its JSON fits `budget`, in the same fixed order
 * the local store uses: oldest message first, traces before whole run payloads.
 * Nothing is ever truncated mid-message — a receipt either keeps its evidence
 * or says (through traceOmitted / runShed, which the flow view already reads)
 * that it lost it.
 *
 * Used before a cloud write. The table splits a large thread across sibling
 * items, so this only bites on a thread too big even for that — the honest
 * floor, reached by shedding evidence that announces itself, never by dropping
 * messages the user wrote.
 *
 * @returns {{convo: object, shed: boolean, size: number}}
 */
export function fitConversation(convo, budget = MAX_BODY_CHARS) {
  let json = JSON.stringify(convo)
  if (json.length <= budget) return { convo, shed: false, size: json.length }

  const next = { ...convo, messages: (convo.messages || []).slice() }
  const targets = []
  for (let m = 0; m < next.messages.length; m += 1) {
    if (next.messages[m]?.role === 'run') targets.push(m)
  }
  let shed = false
  for (let m = 0; m < next.messages.length && json.length > budget; m += 1) {
    const patched = shedDebug(next.messages[m])
    if (patched === next.messages[m]) continue
    next.messages[m] = patched
    shed = true
    json = JSON.stringify(next)
  }
  for (const step of [shedTrace, shedRun]) {
    for (const m of targets) {
      if (json.length <= budget) break
      const patched = step(next.messages[m])
      if (patched === next.messages[m]) continue
      next.messages[m] = patched
      shed = true
      json = JSON.stringify(next)
    }
  }
  return { convo: shed ? next : convo, shed, size: json.length }
}

export function saveConversations(scope, conversations) {
  // Keep the store bounded — old threads are the first thing to go when a
  // browser's quota runs out, and losing them silently is worse than capping.
  // Losing them is also no longer permanent: the cloud keeps what the cache
  // sheds, and the next sync brings it back — WHICH IS ONLY TRUE OF A THREAD
  // THE ACCOUNT ACTUALLY HOLDS, so the cap evicts those first and keeps the
  // rest. See capToCache.
  const list = Array.isArray(conversations) ? conversations : []
  const capped =
    list.length > CONVO_LIMIT ? capToCache(list, holderFor(scope)) : list.slice(0, CONVO_LIMIT)
  try {
    localStorage.setItem(key(scope), fit(capped, STORE_BUDGET))
  } catch {
    // Quota or private mode. Try once more with everything sheddable shed
    // rather than leaving the thread list stuck at its last good write.
    try {
      localStorage.setItem(key(scope), fit(capped, 0))
    } catch {
      /* private mode — the session still works, it just won't persist */
    }
  }
}

export function newConversation() {
  // A real UUID, because the id is now the conversation's URL (/chat/<id>) and
  // a link somebody pastes should look like one. Old `c_…` ids stay valid —
  // the id is opaque everywhere it is read.
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
  return {
    id,
    title: '',
    messages: [],
    updatedAt: Date.now(),
  }
}

// ── cloud sync ─────────────────────────────────────────────────────────────
//
// THE RULE: localStorage is a CACHE, the table is the source of truth.
//
// The chat page paints from the cache synchronously (instant, offline, no
// spinner) and then reconciles with the account in the background. Everything
// below exists to make that reconciliation safe in the three ways it can go
// wrong:
//
//   1. A FAILED READ IS NOT AN EMPTY ACCOUNT. listRemote() reports ok:false
//      rather than [], and every path here refuses to touch the cache or the
//      cloud on a failed read. One flaky request must never look like "you
//      have no history" and must never delete a row.
//   2. A FAILED WRITE MUST NOT LOSE A THREAD. Writes go through a queue that
//      is persisted to localStorage, so a thread written while offline (or
//      during a deploy) is still queued after a reload and lands when the
//      network comes back.
//   3. A DELETE MUST STICK. A thread removed here leaves a TOMBSTONE, so a
//      sync that races the delete cannot resurrect it, and the DELETE is
//      retried until the server confirms it.
//
// MIGRATION runs ONCE per browser+account: on the first successful read, every
// thread this browser holds that the account does not gets uploaded. That is
// what recovers the threads already sitting in a browser today. It is once and
// not every load because a thread deleted from another device must stay
// deleted rather than being pushed back up forever.

const MIGRATED_SUFFIX = 'migrated'
const QUEUE_SUFFIX = 'queue'
const TOMBS_SUFFIX = 'deleted'
// What the ACCOUNT has been confirmed to hold — id -> the stamp it held it at,
// from a successful read, a confirmed write, or a verified migration upload.
// Persisted because it answers a question the cache alone cannot: a thread that
// is here but NOT in the account is either one we have never uploaded (push it)
// or one another device deleted (never push it again). Without this the two are
// indistinguishable after a reload, and every sync would resurrect what the
// user deleted. The stamp is what keeps that inference safe against a list the
// server truncated — see adoptRemoteDeletes.
const KNOWN_SUFFIX = 'known'

// How many conversations the LIST endpoint will return at most, mirroring the
// handler's MAX_CONVERSATIONS (it sends the newest that many and drops the
// rest). Only relevant to one question — "is this list everything?" — and it is
// answered conservatively: a response holding this many rows may have been cut,
// so absence below the oldest row it did send proves nothing. A response
// holding fewer is complete, which is what lets a delete made on another
// browser be recognised here at all. Mirrored, like MAX_BODY_CHARS; if the
// server's cap ever changes, this changes with it.
const REMOTE_LIST_CAP = 500

// How many threads History shows: everything the account's list can return.
// The merge keeps this many in memory (the server already sent them all); only
// the localStorage write is cut to CONVO_LIMIT, and a thread that drops out of
// the cache is still in the account and comes back on the next sync.
export const VIEW_LIMIT = REMOTE_LIST_CAP

// A delete only needs to outlive the window in which another device could
// still be holding the thread. Thirty days is far past any plausible sync lag,
// and letting tombstones expire keeps the key from growing forever.
const TOMB_TTL_MS = 30 * 24 * 60 * 60 * 1000

// One write per thread per burst. A message, a receipt and an auto-title can
// all land inside two seconds; they should cost one PUT, not three.
const WRITE_DEBOUNCE_MS = 1500
const RETRY_BACKOFF_MS = [4000, 12_000, 30_000, 60_000]

function metaKey(scope, suffix) {
  return `${key(scope)}.${suffix}`
}

function readJson(storageKey, fallback) {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return parsed ?? fallback
  } catch {
    return fallback
  }
}

function writeJson(storageKey, value) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value))
  } catch {
    /* private mode or quota — the in-memory queue still drains this session */
  }
}

// Everything about the sync for ONE scope. Rebuilt from disk whenever the
// signed-in account changes, so a sign-out/sign-in without a reload can never
// drain one account's queue against another's identity.
const sync = {
  scope: null,
  // What the account is KNOWN to hold: id -> updatedAt(ms) as of the last
  // confirmed read or write. A thread whose local stamp is newer than this (or
  // that is missing from it entirely) is what "needs pushing" means.
  baseline: new Map(),
  // A read has succeeded this session. Until it has, we do not know what the
  // account holds, so we queue nothing — the first successful read diffs the
  // whole list and catches up.
  ready: false,
  // id -> { op:'put'|'del', convo }. The convo is held in memory so the write
  // sends the FULL thread rather than the cache's possibly-shed copy.
  queue: new Map(),
  tombs: new Map(),
  // id -> stamp(ms) the account is confirmed to hold it at (see KNOWN_SUFFIX).
  // Persisted.
  known: new Map(),
  // id -> the `title|messageCount` shape we last SENT (or last read back), so a
  // change the stamp does not move — a rename — is still noticed, and a row we
  // already agree with is not re-written on every load.
  sigs: new Map(),
  timer: null,
  inflight: false,
  attempt: 0,
  status: {
    cloud: false,
    state: 'idle',
    pending: 0,
    reason: '',
    lastSyncAt: 0,
  },
  listeners: new Set(),
}

/**
 * FORGET THIS BROWSER'S COPY OF A MEMBER'S SCOPE. Called when a view-as
 * begins. A member's scope in an admin's browser is a VIEW of their cloud
 * history, never the durable home for it — and before this fix it was filled
 * with the ADMIN's own threads (the sync ran bare and cached them under the
 * member's name). Nothing local under a foreign scope is authoritative, so
 * the cache, queue, tombstones and confirmed stamps are dropped and the next
 * act-as read repopulates from the member's real account. The session's own
 * scope is never touched: that IS this browser's durable copy.
 */
export function forgetForeignScope(scope) {
  const name = String(scope || '').trim().toLowerCase()
  const owner = sessionEmail()
  if (!name || name === 'anon' || !owner || name === owner) return false
  try {
    localStorage.removeItem(key(name))
    for (const suffix of [QUEUE_SUFFIX, TOMBS_SUFFIX, KNOWN_SUFFIX]) localStorage.removeItem(metaKey(name, suffix))
  } catch {
    /* private mode or quota — the act-as read still replaces what it can */
  }
  if (sync.scope === name) {
    // The in-memory mirror of that scope goes with it; a drain that was armed
    // for the old contents must not fire against the new.
    sync.scope = null
    if (sync.timer) {
      clearTimeout(sync.timer)
      sync.timer = null
    }
  }
  return true
}

function loadScope(scope) {
  const name = scope || 'anon'
  if (sync.scope === name) return
  sync.scope = name
  sync.baseline = new Map()
  sync.ready = false
  sync.inflight = false
  sync.attempt = 0
  // Per-account, both of them: a signature remembered for one account's thread
  // must never suppress a write for another's, and an id the previous account's
  // rows contained says nothing about this one's.
  sync.sigs = new Map()
  sync.known = readKnown(name)
  sync.queuedSince = 0
  if (sync.timer) {
    clearTimeout(sync.timer)
    sync.timer = null
  }

  const tombs = readJson(metaKey(name, TOMBS_SUFFIX), {})
  sync.tombs = new Map()
  const cutoff = Date.now() - TOMB_TTL_MS
  let pruned = false
  for (const [id, at] of Object.entries(tombs || {})) {
    if (Number(at) > cutoff) sync.tombs.set(id, Number(at))
    else pruned = true
  }
  if (pruned) persistTombs()

  // The queue survives a reload; the bodies do not, so a queued put is
  // reloaded WITHOUT a convo and reads the cache at flush time.
  const queued = readJson(metaKey(name, QUEUE_SUFFIX), null)
  sync.queue = new Map()
  for (const id of Array.isArray(queued?.puts) ? queued.puts : []) {
    sync.queue.set(String(id), { op: 'put', convo: null })
  }
  for (const id of Array.isArray(queued?.dels) ? queued.dels : []) {
    sync.queue.set(String(id), { op: 'del', convo: null })
  }
  setStatus({ cloud: false, state: 'idle', pending: sync.queue.size, reason: '' })
}

function persistQueue() {
  const puts = []
  const dels = []
  for (const [id, entry] of sync.queue) (entry.op === 'del' ? dels : puts).push(id)
  writeJson(metaKey(sync.scope, QUEUE_SUFFIX), { puts, dels })
}

function persistTombs() {
  writeJson(metaKey(sync.scope, TOMBS_SUFFIX), Object.fromEntries(sync.tombs))
}

/** id -> confirmed account stamp, for one scope, off disk. */
function readKnown(scope) {
  const raw = readJson(metaKey(scope, KNOWN_SUFFIX), {})
  const out = new Map()
  for (const [id, at] of Object.entries(raw && typeof raw === 'object' ? raw : {})) {
    if (id) out.set(String(id), Number(at) || 0)
  }
  return out
}

function persistKnown() {
  writeJson(metaKey(sync.scope, KNOWN_SUFFIX), Object.fromEntries(sync.known))
}

/** The shape of a thread as far as "has this changed?" is concerned. */
function sigOf(convo) {
  return `${String(convo?.title || '')}|${(convo?.messages || []).length}`
}

/**
 * PIN THE ACCOUNT AN OPERATION IS FOR.
 *
 * Everything below this line is module state shared by every account that signs
 * in without a reload, and every await is a chance for the account to change
 * underneath it (logout()/login() is pure SPA state — see auth.jsx). An
 * operation that keeps going after that switch is not merely late, it is
 * WRONG in the worst available way: it writes one user's threads into another
 * user's rows, and stamps one user's "migration done" flag under the other's
 * key. So each operation takes a pin — the scope it may touch and the exact ID
 * token every one of its requests must ride (see tokenFor in chatCloud.js) —
 * and checks `live()` after every await. A pin that is no longer live means the
 * operation is VOID: its answer is discarded, nothing is cached, nothing is
 * flagged, nothing is uploaded. Retrying is free (the queue is on disk and the
 * migration flag is unset); leaking is not.
 *
 * THE PIN ALSO CARRIES WHO IS BEING VIEWED AS. While the owner/admin
 * impersonates a member, the chat scope is the member's email and every
 * request must say so (`X-Act-As`, see chatCloud.js) or the server answers
 * with the ADMIN's rows — which the client would then cache under the member's
 * scope, and "his chat" would be the admin's. So `actAs` is captured with the
 * scope and handed to every request, pinned: a request never grows or loses
 * the header because impersonation started or stopped during an await.
 *
 * SAFETY PIN. auth.jsx sets the acting target and the user scope back to back,
 * but this module's `sync.scope` only catches up when the chat page next
 * loads it — so between those moments an operation can see the ADMIN's scope
 * with the MEMBER as its acting target. Such a pin is not live from birth: it
 * would read the member's rows and cache them under the admin's key, then let
 * the migration push the admin's local threads into the member's account.
 * Treated exactly like a scope change: the operation is void.
 */
// getIdToken() refreshes an expired session over the network (Cognito). A
// refresh that never answers would otherwise hold the drain's in-flight flag
// forever — no write in the session would go out again. Bounded here; a
// timeout reads as "no token right now", which the drain reports as signed
// out and retries.
const TOKEN_TIMEOUT_MS = 15_000

async function pinAccount() {
  const scope = sync.scope
  // Read in the same tick as the scope, before the token await: the pair must
  // describe one moment, not one moment and the one after the refresh.
  const actAs = getActingAs()
  let token = ''
  try {
    token =
      (await Promise.race([
        getIdToken(),
        new Promise((resolve) => {
          const t = setTimeout(() => resolve(''), TOKEN_TIMEOUT_MS)
          if (typeof t?.unref === 'function') t.unref()
        }),
      ])) || ''
  } catch {
    token = ''
  }
  // THE PIN IS TWO-DIRECTIONAL. Acting: the target must BE the scope. Not
  // acting: the scope must be the REAL signed-in account's — the one the token
  // belongs to. The first version only checked the acting half, and the
  // review reproduced the gap: Exit impersonation with the chat page
  // unmounted, and sync.scope stays 'bo@…' while the acting target is cleared;
  // the still-armed debounce then drained Bo's queue under the admin's bare
  // token — Bo's thread written into the admin's account, Bo's delete answered
  // 404 by the admin's rows and consumed. A member's scope with nobody acting
  // for it is never live; the queue stays on disk and drains the next time an
  // identity that may act as that member is present.
  const owner = sessionEmail()
  const consistent = actAs ? actAs === scope : Boolean(owner) && scope === owner
  return { scope, token, actAs, live: () => consistent && sync.scope === scope }
}

/**
 * The status sentence for a refusal to act — the server would not open the
 * viewed member's history to this caller. Names the member and quotes the
 * server, and says outright that nothing was substituted: the alternative
 * (retrying without the header and painting the admin's own threads) is the
 * bug this exists to close, so the panel must never imply history is merely
 * "loading".
 */
function actingRefusedText(res) {
  // A deployment that IGNORED the header already carries the whole sentence —
  // it is a different fact from a refusal (nobody said no; the service is
  // simply too old to have been asked), and wrapping it in "the server
  // refused" would send the reader looking for a permission problem that is
  // not there.
  if (res.actingIgnored) return `Viewing as ${res.actAs} — ${res.reason}`
  return `Viewing as ${res.actAs} — the server refused to open their history (${
    res.serverReason || res.reason || 'no reason given'
  }); nothing is shown in its place.`
}

/**
 * Did an acting request come back as anything other than the member's own
 * rows? A 403 (refused) and an un-echoed 200 (a service too old to act) are
 * different sentences and the SAME decision: show nothing, cache nothing,
 * never fall back to the caller's own history.
 */
function actingFailed(res) {
  return Boolean(res && res.actAs && (res.status === 403 || res.actingIgnored))
}

/**
 * The shape syncConversations returns for an operation it abandoned.
 *
 * `ok:false` on purpose: the caller's contract for a non-ok read is already
 * "keep painting the cache, change nothing anywhere", which is exactly right
 * for an answer that belongs to an account that is no longer signed in. The
 * status is deliberately NOT touched — it now describes the new account.
 */
function abandoned(list) {
  return {
    ok: false,
    conversations: list,
    uploaded: 0,
    migrated: false,
    abandoned: true,
    reason: '',
  }
}

function setStatus(patch) {
  sync.status = { ...sync.status, ...patch, pending: sync.queue.size }
  for (const fn of sync.listeners) {
    try {
      fn(sync.status)
    } catch {
      /* a broken subscriber must not break the sync */
    }
  }
}

/** Subscribe to sync status. Returns the unsubscribe function. */
export function subscribeChatSync(fn) {
  sync.listeners.add(fn)
  return () => sync.listeners.delete(fn)
}

/**
 * What the sync is doing right now, for an honest affordance in the UI.
 *
 * `state` is one of:
 *   idle     — nothing pending (and, if `cloud`, the account has been read)
 *   syncing  — a request is in flight
 *   error    — a write failed and is queued for retry; `reason` says why
 *   offline  — the account could not be read; we are running from cache
 *   signedOut — no token, so there is nothing to sync to
 * `cloud` is the only thing that licenses the UI to claim history is saved to
 * the account: it is true only after the server actually answered.
 */
export function chatSyncStatus() {
  return sync.status
}

/**
 * HOW COMPLETE one copy of a thread is, as a comparable pair.
 *
 * Two copies of the same thread at the same stamp are not always the same
 * thread: the cache is shed to fit the browser's quota (see fit/shedTrace),
 * which drops per-lead traces and whole run payloads while keeping every
 * message. That copy is a SUBSET of the one in the account, and a stamp cannot
 * tell them apart — so it is measured instead.
 *
 *   messages — a copy with fewer messages is missing some of them
 *   evidence — receipts that still carry their run payload and their trace;
 *              each shed step announces itself (traceOmitted, runShed), so
 *              this counts what is really still there, never a guess at size
 */
function completeness(convo) {
  const messages = Array.isArray(convo?.messages) ? convo.messages : []
  let evidence = 0
  for (const m of messages) {
    if (m?.role !== 'run') continue
    if (m.result && !m.runShed) evidence += 1
    const trace = m.result?.trace
    if (trace && typeof trace === 'object' && Object.keys(trace).length) evidence += 1
  }
  return [messages.length, evidence]
}

/** True when `a` holds strictly more of the thread than `b` does. */
function moreComplete(a, b) {
  const [am, ae] = completeness(a)
  const [bm, be] = completeness(b)
  if (am !== bm) return am > bm
  return ae > be
}

/**
 * Keep at most CONVO_LIMIT threads — BUT EVICT ONLY WHAT THE ACCOUNT ALREADY
 * HOLDS.
 *
 * The cap exists so one browser's store cannot grow without bound, and it was
 * safe on the promise that "the cloud keeps what the cache sheds". That promise
 * is broken for a thread the account does NOT hold yet: this browser's copy is
 * the only copy in existence, and dropping it off the end of the list — which
 * is exactly what happened to the oldest local-only threads the moment a
 * fuller account was read — loses it permanently. So the eviction order is:
 * anything the account is confirmed to hold, oldest first; a thread we cannot
 * prove is safe elsewhere is kept, and only when every thread is at risk does
 * this fall back to plain truncation.
 *
 * Order-preserving: the caller decides the order (newest first everywhere in
 * this file), and this only chooses what to drop out of it.
 *
 * `limit` is CONVO_LIMIT for the localStorage write and VIEW_LIMIT for the
 * list on screen (mergeConversations).
 */
function capToCache(list, holder = sync, limit = CONVO_LIMIT) {
  if (list.length <= limit) return list
  const safe = (c) => holder.baseline.has(c.id) || holder.known.has(c.id)
  const risky = list.filter((c) => c && c.id && !safe(c))
  if (!risky.length) return list.slice(0, limit)
  const keep = new Set(risky.slice(0, limit).map((c) => c.id))
  for (const c of list) {
    if (keep.size >= limit) break
    if (c && c.id) keep.add(c.id)
  }
  return list.filter((c) => c && keep.has(c.id))
}

/**
 * What is known about ONE scope's account, whether or not it is the scope the
 * sync currently has loaded. Reading the persisted `known` ids costs one
 * localStorage hit and is only ever asked for when a list is over the cap.
 */
function holderFor(scope) {
  const name = scope || 'anon'
  if (sync.scope === name) return sync
  return { baseline: new Map(), known: readKnown(name) }
}

/**
 * Merge two thread lists by id, newest `updatedAt` winning per conversation.
 *
 * A TIE keeps the local copy — the two are the same thread at the same stamp,
 * and the local one may already carry an edit that has not been re-stamped —
 * UNLESS the other copy is strictly more complete, in which case the tie is not
 * between two versions of a thread but between a thread and a subset of it.
 * Nothing is ever dropped for being absent from one side — a thread only in the
 * cloud is a thread from another browser, and a thread only here is one this
 * browser has not uploaded yet. Both belong in the result.
 *
 * Passing `scope` additionally drops any thread this browser has a tombstone
 * for, so neither a read that raced a delete nor a local copy of a thread
 * deleted elsewhere can resurrect it.
 */
export function mergeConversations(local, remote, scope = null) {
  if (scope !== null) loadScope(scope)
  const byId = new Map()
  for (const c of Array.isArray(local) ? local : []) {
    if (!c || typeof c.id !== 'string' || !c.id) continue
    // A tombstone covers OUR copy too. The thread was deleted — here, or on
    // another browser of the same account, which a successful read that no
    // longer lists it proves (see adoptRemoteDeletes) — so keeping it on screen
    // would show a thread that no longer exists and can never sync again.
    if (scope !== null && sync.tombs.has(c.id)) continue
    byId.set(c.id, c)
  }
  for (const r of Array.isArray(remote) ? remote : []) {
    if (!r || typeof r.id !== 'string' || !r.id) continue
    if (scope !== null && sync.tombs.has(r.id)) continue
    const mine = byId.get(r.id)
    if (!mine) {
      byId.set(r.id, r)
      continue
    }
    // An INCOMPLETE remote row never wins, however new its stamp. `damaged`
    // and `omitted` both arrive with `messages: []` and a real `updatedAt`, so
    // by stamp alone they would beat the full local copy and blank the thread —
    // and the blank version is what the next write would push back over the
    // server's good rows. A copy that admits it is not the whole thread cannot
    // be allowed to replace one that is.
    if (r.incomplete) continue
    // The mirror image of the guard above, and the one that stranded a thread
    // as permanently empty: when OUR copy is the placeholder — an `incomplete`
    // row a previous sync stored, or a shed copy with no messages left — a
    // remote copy that really has messages must win even on an EQUAL stamp.
    // Ties otherwise keep `mine` (correct for two real copies of the same
    // thread), which meant the empty one kept winning every subsequent sync and
    // the history never came back.
    const mineEmpty = Boolean(mine.incomplete) || (mine.messages || []).length === 0
    const remoteHas = (r.messages || []).length > 0
    if (mineEmpty && remoteHas) {
      byId.set(r.id, r)
      continue
    }
    const rAt = msOf(r.updatedAt)
    const mineAt = msOf(mine.updatedAt)
    if (rAt > mineAt) {
      byId.set(r.id, r)
      continue
    }
    // EQUAL STAMPS, and both copies have messages. Keeping `mine` is right for
    // two real copies of the same thread (a rename that never moved the stamp
    // lives in the local one) — but the local copy may be the SHED one, whole
    // in message count and hollowed out of its evidence to fit the browser's
    // quota. Preferring it would take the traces off the screen and, on the
    // next keystroke, out of the account. Whichever copy holds strictly more
    // wins; a tie that is genuinely a tie still keeps `mine`.
    byId.set(r.id, rAt === mineAt && moreComplete(r, mine) ? r : mine)
  }
  // The list ON SCREEN: VIEW_LIMIT, not the cache's CONVO_LIMIT.
  return capToCache(
    [...byId.values()].sort((a, b) => msOf(b.updatedAt) - msOf(a.updatedAt)),
    sync,
    VIEW_LIMIT,
  )
}

/**
 * Read the account, merge it over what this browser holds, and (once) upload
 * anything the account is missing.
 *
 * Returns { ok, conversations, uploaded, migrated, reason }. On a failed read
 * `ok:false` and `conversations` is the list it was given, UNCHANGED — the
 * caller keeps painting the cache, nothing is deleted anywhere, and no
 * migration is recorded, so the upload still happens on a later load.
 */
export async function syncConversations(scope, local) {
  loadScope(scope)
  const list = Array.isArray(local) ? local : []
  setStatus({ state: 'syncing' })

  // Everything from here on belongs to THIS account and rides THIS token.
  const pin = await pinAccount()
  if (!pin.live()) return abandoned(list)

  const res = await listRemote({ token: pin.token, actAs: pin.actAs })
  // The answer arrived for an account that is no longer signed in. It is not
  // merged, not cached, not diffed, and no flag is written: those rows and this
  // browser's `sync` state now belong to different users.
  if (!pin.live()) return abandoned(list)
  if (!res.ok) {
    // The viewed member's history was refused to this caller. Reported as an
    // error in its own words — NOT as "offline" (the account was reached and
    // said no) and never by falling back to the caller's own rows, which is
    // what used to put the admin's threads on screen under the member's name.
    if (actingFailed(res)) {
      const reason = actingRefusedText(res)
      setStatus({ state: 'error', reason })
      return { ok: false, conversations: list, uploaded: 0, migrated: false, reason }
    }
    setStatus({
      state: res.auth === false ? 'signedOut' : 'offline',
      reason: res.reason || '',
    })
    return { ok: false, conversations: list, uploaded: 0, migrated: false, reason: res.reason || '' }
  }

  // A row this browser knew the account held and the account no longer lists
  // was deleted somewhere else. Adopt that decision before anything else looks
  // at the list, so nothing downstream tries to push it back.
  adoptRemoteDeletes(res.conversations)
  const remote = res.conversations.filter((c) => !sync.tombs.has(c.id))
  // The baseline (and the sigs read back off it) BEFORE the merge: the cap the
  // merge applies has to know which threads are safe in the account and which
  // exist only here.
  sync.baseline = new Map(remote.map((c) => [c.id, msOf(c.updatedAt)]))
  for (const c of remote) {
    sync.known.set(c.id, msOf(c.updatedAt))
    // A row we have just read is a row we agree with, so it is not a change to
    // write back. Never seeded from an incomplete row — we did not see that
    // thread, only the fact that it exists.
    if (!c.incomplete) sync.sigs.set(c.id, sigOf(c))
  }
  persistKnown()
  // WITH the scope: a thread this browser has a tombstone for — its own delete,
  // or one adopted from the account a moment ago — must not come back through
  // the LOCAL side of the merge either.
  const merged = mergeConversations(list, remote, scope)
  sync.ready = true
  setStatus({ cloud: true, state: 'idle', reason: '', lastSyncAt: Date.now() })

  const { list: reconciled, repair, abandoned: gone } = await reconcileIncomplete(remote, merged, pin)
  if (gone || !pin.live()) return abandoned(list)

  // The migration diffs against the FULL local list, not the capped merge: the
  // threads it exists to rescue are the oldest ones this browser holds, which
  // is precisely what a cap drops.
  const migration = await migrateOnce(reconciled, pin, list)
  if (migration.abandoned || !pin.live()) return abandoned(list)
  // AFTER migrateOnce, which re-seeds the baseline from the merged list and
  // would otherwise re-mark these as already-synced: a damaged thread's broken
  // row carries the newer stamp, so only forgetting it makes queueChanged see
  // the local copy as the one to write.
  for (const id of repair) {
    sync.baseline.delete(id)
    sync.sigs.delete(id)
  }
  // Anything left whose local stamp beats the account's (an edit made in this
  // browser while it was last offline) is queued as a normal write.
  queueChanged(scope, reconciled)
  return {
    ok: true,
    conversations: reconciled,
    uploaded: migration.uploaded,
    migrated: migration.migrated,
    reason: '',
  }
}

/**
 * Take the account's word for a thread it no longer lists.
 *
 * This is the other half of "a delete must stick". The tombstone that makes a
 * delete stick is written by the browser that performed it; a SECOND browser of
 * the same account has no tombstone, only a cached copy of a thread that is now
 * gone from the account — and the moment its user touches that thread, the
 * write queue puts it back and the delete is undone on every device.
 *
 * So a successful read is taken at its word: an id this browser had confirmed
 * the account held (`known`) and the account no longer lists gets the same
 * tombstone a local delete would have written. Only a SUCCESSFUL list does
 * this, and only for ids we had positive prior evidence for — a failed read
 * never reaches here, and a thread we merely hope is up there is never dropped.
 *
 * ABSENCE IS ONLY EVIDENCE FROM A LIST THAT WAS NOT CUT. Size never drops a
 * thread — too big to send comes back LISTED and flagged `omitted` — but COUNT
 * can: the handler sends the newest REMOTE_LIST_CAP and no more. So a response
 * at that cap may have left older threads out, and below its oldest row nothing
 * can be concluded; a shorter response is the whole account, and anything
 * missing from it is missing because it was deleted.
 */
function adoptRemoteDeletes(rows) {
  if (!sync.known.size) return []
  const list = (Array.isArray(rows) ? rows : []).filter((r) => r && r.id)
  const listed = new Set(list.map((r) => r.id))
  // Below this stamp the answer is silent rather than negative. -Infinity when
  // the list cannot have been cut, which is the ordinary case.
  let cut = -Infinity
  if (list.length >= REMOTE_LIST_CAP) {
    cut = Infinity
    for (const r of list) cut = Math.min(cut, msOf(r.updatedAt))
  }
  const gone = [...sync.known]
    .filter(([id, at]) => !listed.has(id) && at > cut)
    .map(([id]) => id)
  if (!gone.length) return []
  for (const id of gone) {
    sync.known.delete(id)
    sync.tombs.set(id, Date.now())
    // A queued write for a thread the account has deleted is a resurrection
    // waiting to happen.
    sync.queue.delete(id)
    sync.baseline.delete(id)
    sync.sigs.delete(id)
  }
  persistKnown()
  persistTombs()
  persistQueue()
  return gone
}

/**
 * Deal with the threads the list could not hand over whole.
 *
 * The API answers `messages: []` plus a flag in two cases, and they want
 * opposite treatment:
 *
 *   `omitted` — the thread is fine, it was just too big for the size-bounded
 *               list response. Fetch it on its own, where no budget applies.
 *   `damaged` — the stored thread is missing a part. If THIS browser still has
 *               a full copy, that copy is the only good one left, so it is
 *               named for repair and the caller pushes it back up.
 *
 * Returns { list, repair } — `repair` being the ids whose local copy should
 * overwrite the account's, which the caller applies after migrateOnce so the
 * baseline re-seed cannot quietly cancel it.
 *
 * Failures here are not fatal: a thread that cannot be fetched stays in the
 * list wearing its flag, which the UI renders as unreadable rather than empty,
 * and which queueChanged refuses to write back.
 */
async function reconcileIncomplete(remote, merged, pin) {
  const flagged = remote.filter((c) => c.incomplete)
  if (!flagged.length) return { list: merged, repair: [] }

  const out = merged.slice()
  const repair = []
  for (const row of flagged) {
    const at = out.findIndex((c) => c.id === row.id)
    if (at < 0) continue
    const held = out[at]
    const haveFullLocal = !held.incomplete && (held.messages || []).length > 0

    if (haveFullLocal) {
      // Only a damaged row needs repairing; an omitted one is intact upstream.
      if (row.incomplete === 'damaged') repair.push(row.id)
      continue
    }

    const one = await getRemote(row.id, { token: pin.token, actAs: pin.actAs })
    // This thread came back for an account that is no longer signed in: it must
    // not reach the list the caller is about to cache.
    if (!pin.live()) return { list: merged, repair: [], abandoned: true }
    if (one.ok && !one.conversation.incomplete) {
      out[at] = one.conversation
      sync.baseline.set(row.id, msOf(one.conversation.updatedAt))
      sync.sigs.set(row.id, sigOf(one.conversation))
      sync.known.set(row.id, msOf(one.conversation.updatedAt))
    }
  }
  return { list: out, repair }
}

/**
 * The once-per-browser-and-account upload of local-only threads.
 *
 * This is the whole recovery story for a browser that already has history:
 * every thread here that the account does not hold goes up in one bulk PUT.
 *
 * WHAT COUNTS AS LOCAL-ONLY. Every thread this browser holds — the full list
 * as of the read, NOT the capped merge. The cap keeps the newest VIEW_LIMIT for
 * the screen, and the threads this exists to rescue are the OLDEST ones: a browser
 * whose account already holds that many newer threads would otherwise diff an
 * orphan-free list, find nothing to upload, mark itself migrated and let the
 * orphans fall out of the cache — gone from both places, permanently.
 *
 * WHAT THE FLAG MEANS: a successful read showed nothing left for this browser
 * to hand over. It is therefore never written by the pass that uploaded — the
 * proof that a thread reached the account comes from a read, and a read taken
 * on the next load is one the write cannot have coloured. The cost is at most
 * one repeated bulk upsert (same ids, same rows, idempotent); the cost of the
 * opposite mistake is a browser that believes it handed over history it never
 * did. What stops that repeat from resurrecting a thread deleted on another
 * device is not the flag but `known` — see adoptRemoteDeletes.
 *
 * A failed or unverified upload always leaves the flag unset and queues the
 * threads individually, so the next load tries again.
 */
async function migrateOnce(merged, pin, localAll = []) {
  // Every write below is keyed on the pinned scope, never on `sync.scope`:
  // module state, and an account switch mid-flight would otherwise stamp this
  // user's "migration done" flag under the NEXT user's key — marking a browser
  // migrated whose threads were never uploaded, which is the one mistake that
  // loses history silently and forever.
  const flagKey = metaKey(pin.scope, MIGRATED_SUFFIX)
  const markDone = () => {
    if (!pin.live()) return
    try {
      localStorage.setItem(flagKey, '1')
    } catch {
      /* the flag can wait for a load that can write */
    }
  }
  const done = (() => {
    try {
      return localStorage.getItem(flagKey) === '1'
    } catch {
      return false
    }
  })()

  // Everything this browser holds, by id: the merged list first (it carries the
  // fullest copy of each thread) and then anything the cap left out of it.
  const pool = new Map()
  for (const c of merged) if (c && c.id) pool.set(c.id, c)
  for (const c of Array.isArray(localAll) ? localAll : []) {
    if (c && c.id && !pool.has(c.id)) pool.set(c.id, c)
  }

  if (done) {
    // Already migrated: whatever is local-only now is either a thread deleted
    // elsewhere or one the write queue owns. Either way it is not the bulk
    // upload's business, so it joins the baseline and is left alone.
    for (const c of merged) {
      if (!sync.baseline.has(c.id)) sync.baseline.set(c.id, msOf(c.updatedAt))
    }
    return { migrated: false, uploaded: 0 }
  }

  const missing = [...pool.values()].filter(
    (c) =>
      !sync.baseline.has(c.id) &&
      !sync.tombs.has(c.id) &&
      // A row the server told us it could not hand over whole is not evidence
      // of anything, and must never be pushed back as if it were.
      !c.incomplete &&
      (c.messages || []).length > 0,
  )
  if (!missing.length) {
    // Nothing to hand over, and a read just proved it. THIS is what sets the
    // flag — including the load after the one that uploaded.
    markDone()
    for (const c of merged) {
      if (!sync.baseline.has(c.id)) sync.baseline.set(c.id, msOf(c.updatedAt))
    }
    return { migrated: true, uploaded: 0 }
  }

  setStatus({ state: 'syncing' })
  const payload = missing.map((c) => fitConversation(c).convo)
  const res = await putRemoteMany(payload, { token: pin.token, actAs: pin.actAs })
  if (!pin.live()) return { migrated: false, uploaded: 0, abandoned: true }
  if (!res.ok) {
    const reason =
      actingFailed(res) ? actingRefusedText(res) : res.reason || 'History could not be uploaded.'
    setStatus({ state: 'error', reason })
    return { migrated: false, uploaded: Number(res.saved) || 0, reason }
  }
  // TRUST NOTHING — READ IT BACK. `ok` only means the request did not fail;
  // it is not proof the rows landed. A transport that reported success while
  // saving nothing (a chunk silently dropped, a partial batch) would have set
  // the flag and stranded every local-only thread forever, which is exactly
  // the failure this whole feature exists to prevent. So: confirm the count the
  // server claims, then re-read the account and require every thread we
  // uploaded to actually be there WITH messages. Only then is the browser
  // migrated.
  const claimed = Number(res.saved)
  if (Number.isFinite(claimed) && claimed < payload.length) {
    setStatus({
      state: 'error',
      reason: `Only ${claimed} of ${payload.length} conversations were saved — will retry.`,
    })
    return { migrated: false, uploaded: claimed, reason: 'partial upload' }
  }

  // The verification rides the SAME pinned token and acting target as the
  // upload. Reading back from whatever account happens to be signed in (or
  // viewed as) a moment later would prove nothing about where those rows went.
  const check = await listRemote({ token: pin.token, actAs: pin.actAs })
  if (!pin.live()) return { migrated: false, uploaded: 0, abandoned: true }
  if (!check.ok) {
    // We cannot prove the upload landed, so we do not claim it did. The flag
    // stays unset and the next load re-runs this — a duplicate upload is
    // harmless (same ids, upsert), a lost thread is not.
    setStatus({
      state: 'error',
      reason: 'Uploaded, but the account could not be re-read to confirm — will retry.',
    })
    return { migrated: false, uploaded: payload.length, reason: 'unconfirmed upload' }
  }
  const landed = new Map(
    (check.conversations || []).map((c) => [
      c.id,
      // Present WITH a body. `omitted` counts: the list only sheds a thread's
      // messages when they were too big to include, which is proof the body is
      // there. `damaged` does not — that row is missing one of its parts.
      ((c.messages || []).length > 0 && !c.incomplete) || c.incomplete === 'omitted',
    ]),
  )
  const absent = missing.filter((c) => !landed.get(c.id))
  if (absent.length) {
    setStatus({
      state: 'error',
      reason: `${absent.length} conversation${absent.length === 1 ? '' : 's'} did not reach your account — will retry.`,
    })
    // Queue them individually: the bulk path evidently did not take, and a
    // per-thread PUT with its own retry/backoff is the more durable route.
    for (const c of absent) sync.queue.set(c.id, { op: 'put', convo: fitConversation(c).convo })
    persistQueue()
    schedule(WRITE_DEBOUNCE_MS)
    return { migrated: false, uploaded: missing.length - absent.length, reason: 'upload unverified' }
  }

  // Proven present, by a read: record it as the account's, so the repeat of
  // this pass on the next load has nothing left to send (which is what sets the
  // flag) and so a later read that no longer lists one of these is read as the
  // delete it is rather than as a thread still waiting to go up.
  for (const c of missing) {
    sync.baseline.set(c.id, msOf(c.updatedAt))
    sync.known.set(c.id, msOf(c.updatedAt))
    sync.sigs.set(c.id, sigOf(c))
  }
  for (const c of merged) {
    if (!sync.baseline.has(c.id)) sync.baseline.set(c.id, msOf(c.updatedAt))
  }
  persistKnown()
  setStatus({ state: 'idle', reason: '', lastSyncAt: Date.now() })
  return { migrated: true, uploaded: missing.length }
}

/**
 * Queue a write for every thread whose local copy is newer than the account's.
 *
 * Called on every change to the list, which is why it is a DIFF rather than a
 * per-callsite hook: a receipt posted by a run, an auto-title landing, a
 * rename and a message all move `updatedAt`, and none of them can forget to
 * sync. Returns how many were queued.
 *
 * Before the first successful read it queues nothing and says so (0): we do
 * not know what the account holds, and pushing the whole cache blind is the
 * migration's job, gated by its own flag.
 */
export function queueChanged(scope, conversations) {
  loadScope(scope)
  // Deliberately NOT gated on `sync.ready`. A change made before the first
  // successful read used to return 0 and vanish — the thread existed only in
  // the cache and nothing ever pushed it. The queue is persisted and flushed
  // later, so queuing early costs at worst one redundant PUT; dropping costs
  // the thread.
  let queued = 0
  for (const convo of Array.isArray(conversations) ? conversations : []) {
    if (!convo || typeof convo.id !== 'string' || !convo.id) continue
    if (sync.tombs.has(convo.id)) continue
    // Never write back a thread the server told us we do not hold in full.
    // A `damaged`/`omitted` placeholder carries no messages; PUTting it would
    // replace the real rows (and sweep the split parts) with nothing.
    if (convo.incomplete) continue
    const known = sync.baseline.get(convo.id)
    const stamp = msOf(convo.updatedAt)
    const pending = sync.queue.get(convo.id)
    // The stamp alone is not enough. Renaming a thread changes its title
    // without necessarily moving updatedAt, so a stamp-only check skipped the
    // write and the new title never left the browser. Compare the shape we
    // last SENT as well: title and message count are cheap and catch every
    // edit the UI can make.
    const sig = sigOf(convo)
    const sentSig = sync.sigs.get(convo.id)
    const unchanged = sentSig !== undefined && sentSig === sig
    if (known !== undefined && known >= stamp && unchanged && !pending) continue
    sync.queue.set(convo.id, { op: 'put', convo })
    sync.sigs.set(convo.id, sig)
    queued += 1
  }
  if (queued) {
    persistQueue()
    schedule(WRITE_DEBOUNCE_MS)
  }
  return queued
}

/**
 * Delete a thread from the account, and remember that we did.
 *
 * The tombstone is written FIRST and synchronously: if the request fails (or
 * the tab closes before it runs) the thread must still not come back on the
 * next sync. The DELETE stays queued until the server confirms it.
 */
export function queueConversationDelete(scope, id) {
  loadScope(scope)
  const key = String(id || '')
  if (!key) return
  sync.tombs.set(key, Date.now())
  persistTombs()
  sync.baseline.delete(key)
  sync.sigs.delete(key)
  // The account is about to stop holding it, so this browser must stop
  // believing it does — otherwise the next read would see a thread it "knew"
  // was up there, no longer listed, and adopt its own delete a second time.
  if (sync.known.delete(key)) persistKnown()
  sync.queue.set(key, { op: 'del', convo: null })
  persistQueue()
  // No debounce: a delete is a decision, not a keystroke.
  schedule(0)
}

// A commit that keeps arriving must not keep a write from ever leaving. The
// debounce resets on every queueChanged, and a receipt whose drafts land one
// by one for a minute (Chat.jsx's draft resolver) commits every second or two
// — long enough that a pure trailing debounce would not PUT once until the
// last draft, and a tab closed midway would carry nothing to the account. So
// the wait is capped: however busy the thread, a drain starts within this much
// of the FIRST queued change.
const MAX_QUEUE_LATENCY_MS = 8_000

// `bounded` (the default) is the debounce path — the cap above applies. A
// RETRY after a real failure passes bounded:false: its backoff must be
// honoured in full, or a refused write would be re-sent instantly forever.
function schedule(delay, { bounded = true } = {}) {
  if (sync.timer) clearTimeout(sync.timer)
  if (sync.queue.size && !sync.queuedSince) sync.queuedSince = Date.now()
  const waited = sync.queuedSince ? Date.now() - sync.queuedSince : 0
  const wait = bounded ? Math.max(0, Math.min(delay, MAX_QUEUE_LATENCY_MS - waited)) : delay
  sync.timer = setTimeout(() => {
    sync.timer = null
    // flush() owns its own failures; the catch is here so a timer callback can
    // never surface as an unhandled rejection in the app.
    flush().catch(() => {})
  }, wait)
  // Node and browsers both allow this; it keeps a pending sync from holding a
  // test process open.
  if (typeof sync.timer?.unref === 'function') sync.timer.unref()
  setStatus({})
}

/** Drain the queue now (used on `online`, on tab focus, and by the harness). */
export async function flushChatSync(scope) {
  if (scope !== undefined) loadScope(scope)
  if (sync.timer) {
    clearTimeout(sync.timer)
    sync.timer = null
  }
  return flush()
}

/**
 * Does this copy admit it was shed to fit the browser's quota?
 *
 * Both shedders leave a mark ON THE MESSAGE — `traceShed` counts the traces a
 * receipt lost, `runShed` says the whole payload went — and nothing else ever
 * sets either, so this is a copy's own admission rather than a guess from its
 * size. It is only ever asked of the CACHE's copy, where the answer decides
 * whether that copy is fit to be written to the account.
 */
function looksShed(convo) {
  return (convo?.messages || []).some(
    (m) => m?.role === 'run' && (m.runShed === true || Number(m.traceShed) > 0),
  )
}

function cachedConversation(id) {
  try {
    const raw = JSON.parse(localStorage.getItem(key(sync.scope)) || '[]')
    return (Array.isArray(raw) ? raw : []).find((c) => c?.id === id) || null
  } catch {
    return null
  }
}

async function flush() {
  if (sync.inflight) return sync.status
  if (!sync.queue.size) {
    setStatus({ state: sync.status.cloud ? 'idle' : sync.status.state, reason: '' })
    return sync.status
  }
  sync.inflight = true
  setStatus({ state: 'syncing' })

  let failed = 0
  let reason = ''
  let signedOut = false
  // Writes held back for one round (see the shed-cache-copy guard below).
  let deferred = 0
  // Set when the account changed under the drain. Everything after that point
  // is another user's: their queue, their keys, their rows.
  let voided = false

  // Which account this whole drain belongs to. Claimed BEFORE the first
  // request, so every write in it rides one token — the alternative is a drain
  // that starts as one user and finishes as the next, posting the threads it
  // has already read out of the first user's cache into the second user's rows.
  const pin = await pinAccount()
  // NEVER WRITE INTO A MEMBER'S ACCOUNT BEFORE READING IT. When acting, the
  // first successful act-as read (sync.ready) is what proves the server will
  // honour the header AND what replaces whatever this browser held under the
  // member's scope with the member's real threads. Draining before that could
  // push a stale local copy — on a pre-fix browser, the admin's own threads
  // cached under the member's name — into the member's account under an
  // audit stamp. The queue waits; it is on disk and loses nothing.
  if (pin.actAs && !sync.ready) {
    sync.inflight = false
    setStatus({ state: 'idle', reason: `Viewing as ${pin.actAs} — waiting for their history to load before saving.` })
    return sync.status
  }
  if (!pin.live()) {
    // A pin can be dead on arrival without the scope having moved: the
    // acting/scope race the pin comment describes. Nothing below ran, so the
    // in-flight flag is still THIS scope's to release — leaving it set (the
    // scope-change path may, because loadScope resets it for the next account)
    // would hold every later drain of this scope until something reloads it,
    // and a queued thread would sit unsaved for the rest of the session.
    if (sync.scope === pin.scope) sync.inflight = false
    return sync.status
  }

  // The whole drain is wrapped: an unexpected throw anywhere in here used to be
  // able to leave `inflight` stuck true, which would silently kill syncing for
  // the rest of the session — a thread that looked saved and never was. On a
  // throw the queue is left exactly as it stands and the retry is scheduled.
  try {
    for (const [id, entry] of [...sync.queue]) {
      if (!pin.live()) {
        voided = true
        break
      }
      if (entry.op === 'del') {
        const res = await deleteRemote(id, { token: pin.token, actAs: pin.actAs })
        if (!pin.live()) {
          voided = true
          break
        }
        if (res.ok) {
          if (sync.queue.get(id) === entry) sync.queue.delete(id)
          sync.baseline.delete(id)
        } else if (res.auth === false) {
          signedOut = true
          reason = res.reason || ''
        } else {
          failed += 1
          reason = actingFailed(res) ? actingRefusedText(res) : res.reason || ''
        }
        continue
      }
      // A thread the user deleted is never written back, whatever is queued for
      // it. (adoptRemoteDeletes can tombstone an id that already had a pending
      // write when the account's answer said it was gone.)
      if (sync.tombs.has(id)) {
        sync.queue.delete(id)
        continue
      }
      // The in-memory copy is the full thread; the cache's copy may have been
      // shed to fit the browser quota, and after a reload it is all we have.
      const convo = entry.convo || cachedConversation(id)
      if (!convo) {
        // Nothing to send: the thread is gone from both memory and cache
        // without a delete having been asked for (a 50-thread eviction).
        // Dropping the queued write is right — evicting from the cache must
        // never delete the account's copy.
        sync.queue.delete(id)
        continue
      }
      // A body that came from the CACHE and admits it was shed is a SUBSET of
      // what the account is likely to hold: the write that queued it had the
      // full thread in memory, and only a reload in between lost that. Sending
      // it now would replace good rows with an evidence-free copy — so it waits
      // for the account read that is already in flight, which puts the full
      // thread back in front of the queue (the entry is still pending, so the
      // next commit re-queues it WITH its body). Once, and only before the
      // first read of the session: a deferral that could repeat would be a
      // write that never lands, which is worse than a thin one.
      if (!entry.convo && !entry.deferred && !sync.ready && looksShed(convo)) {
        entry.deferred = true
        deferred += 1
        continue
      }
      const res = await putRemote(fitConversation(convo).convo, { token: pin.token, actAs: pin.actAs })
      // The row landed in the account this write was issued for — but if that
      // is no longer the account this browser is signed in to, none of the
      // bookkeeping below is ours to write.
      if (!pin.live()) {
        voided = true
        break
      }
      if (res.ok) {
        // ONLY THIS ENTRY IS DONE. The queue is live while the PUT is on the
        // wire: a commit that lands mid-flight (a draft resolving, a rename)
        // replaces the entry under the same id with a NEWER body. Deleting by
        // id here used to throw that newer body away — the account kept the
        // version that had left a second earlier and the last change of a
        // thread simply never arrived (a receipt stored with fifty empty draft
        // slots while the browser showed fifty drafts). The newer entry stays;
        // the next drain carries it.
        if (sync.queue.get(id) === entry) sync.queue.delete(id)
        sync.baseline.set(id, msOf(convo.updatedAt))
        sync.sigs.set(id, sigOf(convo))
        sync.known.set(id, msOf(convo.updatedAt))
        persistKnown()
      } else if (res.auth === false) {
        signedOut = true
        reason = res.reason || ''
      } else {
        failed += 1
        // A refusal to act is named as such; the retry keeps the header (it
        // is the member's queue being drained) and never lands the write in
        // the caller's own account instead.
        reason = actingFailed(res) ? actingRefusedText(res) : res.reason || ''
      }
    }
  } catch (err) {
    failed += 1
    // Name the failure. A bare "could not be saved" told nobody anything —
    // the console gets the object, the panel gets its name and message.
    // eslint-disable-next-line no-console
    console.error('[chat sync] drain threw', err)
    const named = [err?.name, err?.message].filter(Boolean).join(': ') || String(err || '')
    reason = named ? `History sync hit an error in this browser (${named}).` : 'History sync hit an error in this browser.'
  } finally {
    // NOT on a voided drain: `sync.queue` and every meta key are the NEXT
    // account's now, and loadScope has already reset `inflight` for it. What
    // this drain did not finish is still on the previous account's disk queue
    // and drains when that account is signed in again.
    if (!voided && pin.live()) {
      persistQueue()
      sync.inflight = false
    }
  }
  if (voided || !pin.live()) return sync.status

  if (!sync.queue.size) {
    sync.attempt = 0
    sync.queuedSince = 0
    setStatus({ state: 'idle', reason: '', lastSyncAt: Date.now() })
    return sync.status
  }
  // WORK THAT ARRIVED WHILE THIS DRAIN RAN IS NOT A FAILURE. Every request in
  // it succeeded; the queue is non-empty only because the thread kept changing
  // underneath (a receipt whose fifty drafts land one at a time). This used to
  // fall through to the error branch below — the panel went red with the
  // fallback sentence ("Not saved to your account yet (1 pending) — History
  // could not be saved…"), and the retry backoff climbed to a minute between
  // saves for a thread that had never once been refused. It is still syncing;
  // say so, and go again on the ordinary beat.
  if (!failed && !signedOut && !deferred) {
    sync.attempt = 0
    setStatus({ state: 'syncing', reason: '' })
    schedule(WRITE_DEBOUNCE_MS)
    return sync.status
  }
  if (deferred && !failed && !signedOut) {
    // Nothing failed and nothing is wrong: what is left is waiting for the
    // account read, not for the network. Reported as still working (or idle if
    // the read has already landed), never as an error, and retried on the same
    // beat as an ordinary write.
    sync.attempt = 0
    setStatus({ state: sync.status.cloud ? 'idle' : 'syncing', reason: '' })
    schedule(WRITE_DEBOUNCE_MS)
    return sync.status
  }
  if (signedOut && !failed) {
    // Nothing to retry against RIGHT NOW: the writes stay queued until there
    // is a session again. Reported as signedOut, never as a failure of the
    // network — but retried on the backoff beat all the same, because "no
    // token" is often a refresh that has not landed yet (an hour-old ID token
    // being renewed) rather than a real sign-out, and a queue that only woke
    // on the next keystroke left threads unsaved for as long as nobody typed.
    const wait = RETRY_BACKOFF_MS[Math.min(sync.attempt, RETRY_BACKOFF_MS.length - 1)]
    sync.attempt += 1
    setStatus({ state: 'signedOut', reason })
    schedule(wait, { bounded: false })
    return sync.status
  }
  const wait = RETRY_BACKOFF_MS[Math.min(sync.attempt, RETRY_BACKOFF_MS.length - 1)]
  sync.attempt += 1
  setStatus({ state: 'error', reason: reason || 'History could not be saved to your account.' })
  schedule(wait, { bounded: false })
  return sync.status
}

// ── store-level message primitives ──────────────────────────────────────────
//
// Extracted from what Chat.jsx's unmount salvage proved: a message can be
// written into a conversation with NO page mounted by reading the CURRENT
// store, changing one conversation, and going back through the exact same
// save → queue → flush machinery every mounted commit uses. These are that
// path made first-class, so the run host (lib/runHost.js) can finish work the
// page walked away from. No React, no component refs, and deliberately NO
// second serialization path — everything lands via saveConversations +
// queueChanged, the same two calls the page's own commit effect makes.
//
// THE ORDERING STORY — who wins when the page and the host both write:
//   • Each primitive is a SYNCHRONOUS read-modify-write against the store as
//     it stands at call time (loadConversations → one conversation changes →
//     save). Two primitive calls can therefore never interleave, and a
//     primitive can never write from a stale snapshot the way a component ref
//     could.
//   • The OPEN page keeps its own React copy and re-saves the whole list on
//     every commit, so a page save landing after a host write COULD put the
//     page's older copy of that conversation back in the cache — and React
//     passive effects run after paint, so a host write can land in exactly
//     that gap (between a commit's paint and its save effect), where the
//     broadcast-scheduled merge re-reads the already-clobbered cache and has
//     nothing to restore from. Three things keep that from losing work: the
//     page's commit effect saves the MERGE of its state with the current
//     cache (every primitive stamps `updatedAt`, so mergeConversations
//     carries the newer host write forward instead of shadowing it); the
//     host's stream writer falls back to RE-APPENDING its message, same
//     stamp, when a patch reports the message gone (runHost.js); and the one
//     caller that patches repeatedly (the draft resolver in runHost.js)
//     re-reads the store before every patch and re-applies one that failed to
//     stick, the same settle-and-retry discipline LeadCards used.
//   • subscribeConversations is the broadcast half: it fires after every
//     primitive write so a mounted page knows the store moved underneath it
//     and can re-read before its next save.
//
// HONEST LIMIT: the store is the CACHE, and the cache's copy of a thread may
// have been shed to fit the browser's quota (runShed/traceShed). A primitive
// that appends to a shed copy queues that shed copy for the account, where a
// newer stamp beats a fuller body. The unmount salvage had the same floor
// after a reload; it is accepted here for the same reason — a receipt that
// lands is worth more than evidence that might have.

const convoListeners = new Set()

/**
 * Hear about every store-level message write. `fn` gets ({ scope, convoId })
 * so a mounted page can ignore writes to other users' scopes. Returns the
 * unsubscribe function — same idiom as copiedDrafts/dailyTasks.
 */
export function subscribeConversations(fn) {
  convoListeners.add(fn)
  return () => convoListeners.delete(fn)
}

function announceConversations(scope, convoId) {
  for (const fn of convoListeners) {
    try {
      fn({ scope: scope || 'anon', convoId })
    } catch {
      /* a broken subscriber must not break the write */
    }
  }
}

/**
 * Queue one conversation for a scope that is NOT the one the sync singleton is
 * loaded for. A host write can outlive its account's sign-in ("Sign in as"
 * impersonation, a plain sign-out/sign-in mid-run), and routing it through
 * queueChanged would call loadScope — flipping the module-level sync state
 * back to the old account on every 250ms stream beat and draft patch, which
 * resets baseline/ready and voids the signed-in account's in-flight drains.
 * So the foreign write goes straight onto that scope's PERSISTED queue keys
 * instead: it drains when that account is signed in again (the same promise
 * the drain's own account-switch comment already makes), and the singleton
 * stays on whoever is signed in now. The scope's own tombstones still win —
 * a deleted thread must not be resurrected by a straggling background write.
 */
function queueForeignChange(scope, convoId) {
  const name = scope || 'anon'
  const id = String(convoId || '')
  if (!id) return
  const tombs = readJson(metaKey(name, TOMBS_SUFFIX), {})
  if (tombs && typeof tombs === 'object' && tombs[id]) return
  const queued = readJson(metaKey(name, QUEUE_SUFFIX), null)
  const puts = (Array.isArray(queued?.puts) ? queued.puts : []).map(String)
  const dels = (Array.isArray(queued?.dels) ? queued.dels : []).map(String)
  if (dels.includes(id) || puts.includes(id)) return
  puts.push(id)
  writeJson(metaKey(name, QUEUE_SUFFIX), { puts, dels })
}

/** One write: cache, cloud queue, broadcast — the salvage block's exact path. */
function commitConversations(scope, next, convoId) {
  saveConversations(scope, next)
  if (sync.scope === null || sync.scope === (scope || 'anon')) {
    queueChanged(scope, next)
  } else {
    queueForeignChange(scope, convoId)
  }
  announceConversations(scope, convoId)
}

/**
 * Append one message to a conversation, store-level. Creates the conversation
 * when the store does not hold it yet — the page's own copy of a brand-new
 * thread lives in React state until its commit effect runs, and an append must
 * not be lost to that gap. Returns true when something was written.
 */
export function appendMessage(scope, convoId, message) {
  const id = String(convoId || '')
  if (!id || !message || typeof message !== 'object') return false
  const list = loadConversations(scope)
  const at = list.findIndex((c) => c && c.id === id)
  const now = Date.now()
  let next
  if (at < 0) {
    next = [{ id, title: '', messages: [message], updatedAt: now }, ...list]
  } else {
    next = list.slice()
    next[at] = {
      ...next[at],
      messages: [...(next[at].messages || []), message],
      updatedAt: now,
    }
  }
  commitConversations(scope, next, id)
  return true
}

/**
 * Find a message by its `at` stamp AND role. The role is part of the address
 * on purpose: the contract-blessed parallel case (a stream answering into a
 * thread while a run posts its receipt there) can stamp an assistant message
 * and a receipt in the same millisecond, and a stamp-only lookup would let a
 * stream beat spray { text, nav, debug } onto the receipt. Within one role the
 * LAST match wins on the (millisecond-collision) off chance two share a stamp
 * — the newer message is the one still being written to.
 */
function messageIndexAt(messages, msgAt, role) {
  const stamp = Number(msgAt)
  if (!Number.isFinite(stamp)) return -1
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (Number(messages[i]?.at) === stamp && messages[i]?.role === role) return i
  }
  return -1
}

/**
 * Merge a patch onto one message, addressed by conversation id, the message's
 * own `at` stamp (messages have no other identity that survives a reload) and
 * its role — 'assistant' unless the caller says otherwise, because the stream
 * is the only patcher today and a receipt sharing the stamp must never take
 * the patch (mirrors patchReceiptLead's role === 'run' guard). Race-safe the
 * store-level way: read the CURRENT store, patch, save. Returns true when the
 * message was found and written.
 */
export function patchMessage(scope, convoId, msgAt, patch, role = 'assistant') {
  const id = String(convoId || '')
  if (!id || !patch || typeof patch !== 'object') return false
  const list = loadConversations(scope)
  const at = list.findIndex((c) => c && c.id === id)
  if (at < 0) return false
  const msgs = Array.isArray(list[at].messages) ? list[at].messages : []
  const mi = messageIndexAt(msgs, msgAt, role)
  if (mi < 0) return false
  const nextMsgs = msgs.slice()
  nextMsgs[mi] = { ...nextMsgs[mi], ...patch }
  const next = list.slice()
  next[at] = { ...next[at], messages: nextMsgs, updatedAt: Date.now() }
  commitConversations(scope, next, id)
  return true
}

/**
 * Fill one lead's slot on a run receipt — what Chat.jsx#fillLeadDraft does,
 * store-level. The same two rules, because they are what made the component
 * version safe:
 *
 *   WRITE-ONCE. A slot that already holds an email is not ours to touch,
 *   whatever landed afterwards — a written draft is a record of what may
 *   already have been copied and sent.
 *
 *   THE RECEIPT STOPS ADVERTISING A WAIT once nobody is waiting, so a
 *   reopened conversation does not re-arm the resolver for nothing.
 *
 * Returns true when the patch landed; false when the lead was missing or the
 * write-once guard refused it (the caller's retry loop reads the store again
 * and moves on — see runHost.js).
 */
/**
 * Fill one lead's slot on a receipt, store-level.
 *
 * WRITE-ONCE by default: a lead already holding an email is not touched, which
 * is what lets two resolvers race for the same slot without the second paying
 * for a draft the first already landed.
 *
 * Options, all optional:
 *   replace          the rep's own change to the slot (lib/runHost.js#
 *                    switchLeadRecipient): the write-once guard is waived, and
 *                    a field patched to `undefined` is dropped from the stub
 *                    (so `draft: undefined` reopens the slot and
 *                    `recipient: undefined` puts the card back on the run's own
 *                    recipient — the stub then reads exactly as before).
 *   researchCategory the receipt's category, restored when the receipt lost it
 *                    (it is cleared here once nothing is pending) and a slot is
 *                    being reopened — without it no resolver can write the slot.
 *   forRecipient     the key of the person this patch was written for
 *                    (lib/leadContacts.js#recipientKey). When given as a
 *                    string, the patch lands only if the stub still shows that
 *                    person: an answer written for somebody the rep has since
 *                    switched away from is refused rather than filed under the
 *                    new person's name. Absent or null: not checked.
 */
export function patchReceiptLead(scope, convoId, msgAt, leadKey, leadPatch, options = {}) {
  const { replace = false, researchCategory = '', forRecipient = null } = options || {}
  const id = String(convoId || '')
  const key = String(leadKey || '')
  if (!id || !key || !leadPatch || typeof leadPatch !== 'object') return false
  const list = loadConversations(scope)
  const at = list.findIndex((c) => c && c.id === id)
  if (at < 0) return false
  const msgs = Array.isArray(list[at].messages) ? list[at].messages : []
  const mi = messageIndexAt(msgs, msgAt, 'run')
  if (mi < 0) return false
  const msg = msgs[mi]
  if (!msg || msg.role !== 'run' || !Array.isArray(msg.leads)) return false
  const li = msg.leads.findIndex((l) => l && String(l.key || '') === key)
  if (li < 0) return false
  const lead = msg.leads[li]
  if (!replace && lead.draft?.bodyText) return false
  if (typeof forRecipient === 'string' && currentRecipientOf(lead).key !== forRecipient) return false
  const leads = msg.leads.slice()
  const merged = { ...lead, ...leadPatch }
  if (replace) {
    for (const [k, v] of Object.entries(leadPatch)) if (v === undefined) delete merged[k]
  }
  leads[li] = merged
  const nextMsgs = msgs.slice()
  const pending = leads.some((l) => l?.researchPending || l?.draftPending)
  const kept = String(msg.researchCategory || '').trim()
  nextMsgs[mi] = {
    ...msg,
    leads,
    researchCategory: pending ? kept || String(researchCategory || '').trim().slice(0, 60) : '',
  }
  const next = list.slice()
  next[at] = { ...list[at], messages: nextMsgs, updatedAt: Date.now() }
  commitConversations(scope, next, id)
  return true
}

/**
 * Ask the model for a short title summarising a thread.
 *
 * Reuses the streaming endpoint (there is no separate title route) and just
 * accumulates the tokens — the caller never renders them. Resolves to '' on any
 * failure, because a missing title is invisible (titleFor falls back to the
 * first message) while a thrown error would break the send it rides on.
 *
 * `context` is deliberately NOT passed: naming a thread needs the words in it,
 * not the workspace snapshot, and sending the snapshot would pay for thousands
 * of tokens of grounding to produce four words.
 */
export async function summarizeTitle(messages, { signal } = {}) {
  const transcript = (messages || [])
    .slice(0, 4)
    .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${String(m.text || '').slice(0, 700)}`)
    .join('\n\n')
  if (!transcript.trim()) return ''
  try {
    const { text } = await streamChat({
      messages: [
        {
          role: 'user',
          text:
            'Give this conversation a title of 3 to 6 words that says what it is ABOUT. ' +
            'Reply with the title alone — no quotes, no punctuation at the end, no ' +
            'preamble, and never repeat the question verbatim.\n\n' +
            transcript,
        },
      ],
      context: {},
      signal,
    })
    // Models like to wrap titles in quotes or add a trailing period despite
    // being told not to; strip that rather than storing it.
    return normalizeTitle(String(text || '').replace(/^["'\u201c\u2018]|["'\u201d\u2019.]+$/g, ''))
  } catch {
    return ''
  }
}

// A hand-typed title is capped so one pasted paragraph can't blow out the
// sidebar row (or the localStorage budget every thread shares).
export const TITLE_MAX = 60

/**
 * Clean a user-typed title: collapse whitespace, trim, cap. Returns '' for a
 * blank one, which callers treat as "clear it and go back to deriving the
 * title from the first message" rather than storing an empty string.
 */
export function normalizeTitle(raw) {
  return String(raw || '').replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX)
}

// ── run receipts ────────────────────────────────────────────────────────────
//
// An operation posts its outcome INTO the thread, and the thread is
// localStorage — so the receipt has to carry everything the provenance view
// needs to reopen after a reload (the graph nodes, the result, the per-lead
// trace) and nothing it does not. What packRunMessage() trims, and why:
//
//   • result.selected holds FULL COMPANY ROWS — every CSV column, including the
//     packed contacts and projects blobs. Fifty of those is hundreds of KB of
//     data the view never reads: it wants a count, a name to show, and the key
//     to look a trace up by. They become {key, name, email} stubs, so the count
//     stays honest and every selected lead stays clickable.
//   • trace is kept for every SELECTED lead — those are the rows this receipt
//     makes clickable, and they must always answer. Traces for leads that did
//     not make the cut cannot be reached from here (nothing lists them), so
//     they are dropped and COUNTED into `traceOmitted`, the same field the run
//     uses to report what its own snapshot shed. Never dropped silently.
//   • a run that selected nobody therefore keeps no per-lead trace at all: with
//     no lead rows there is no way in, and the thousands of 'dropped' verdicts
//     behind an empty run are not a receipt. Why it came up empty is on the
//     nodes themselves, which are kept in full.
//   • operation-specific extras the view ignores (operation 3's `replies`) go.
//
// Everything below is read off what the run RECORDED. Nothing is re-derived,
// so a receipt can never contradict the run it describes.

// How many lead rows one receipt lists. Above this the receipt says how many
// more there were rather than growing without bound.
export const RUN_LEAD_LIMIT = 60

// Same idea for the follow-up lanes. Each cap matches (or exceeds) what the
// operations themselves promise to return — calls and manual replies are
// capped at 20 by contract, the worklist caps due items at 40 — so in practice
// nothing is cut; the caps are here so a misbehaving result cannot flood
// localStorage. The receipt stores the TRUE counts alongside, so anything the
// cap did cut is said, never hidden.
// Raised from 12, and no longer a blind prefix slice — see packTasks. Bo's
// live day is 53 rows (38 overdue + 15 due today) and one row is ~200 bytes,
// so the whole list is ~10KB against this receipt's much larger budget.
export const RUN_TASK_LIMIT = 60
const RUN_CALL_LIMIT = 20
export const RUN_WAITING_LIMIT = 40
export const RUN_REPLY_LIMIT = 20
// The customer lanes are capped at 30 upstream (agentRun CUSTOMER_LANE_CAP);
// matching it here means the receipt never silently shows fewer than the run
// chose. (RUN_MEETING_LIMIT went with section 4 on 2026-09-03 — no calendar.)
export const RUN_CUSTOMER_LIMIT = 30

// The node fields the flow view reads. Anything else a pass grew would ride
// along into storage forever.
const PASS_FIELDS = [
  'id',
  'title',
  'subtitle',
  'dependsOn',
  'lane',
  'status',
  'abandoned',
  'headline',
  'detail',
  'metric',
  'startedAt',
  'endedAt',
  // WHERE THE NODE'S CODE LIVES — declared on the node definition in
  // agentRun.js (PASS_DEFS[].exec) and stored with the receipt as it stood
  // when the run executed, so the Developer view can name the file, the
  // functions and the lambdas behind every step of a draft's story even after
  // the code has moved on.
  'exec',
]

function packPass(pass) {
  const out = {}
  for (const field of PASS_FIELDS) {
    const value = pass?.[field]
    if (value !== undefined && value !== null) out[field] = value
  }
  return out
}

// The personalization fields a stub carries forward, with a hard cap per
// field. These are what buildOutreachEmail needs to write (and cite) its one
// personal sentence when a receipt is reopened after a reload. Caps keep 60
// stubs comfortably inside STORE_BUDGET; a field absent on the row is simply
// not stored, so old receipts and sparse rows read exactly as before — no
// personalization, no sources.
// Real messages kept per lead on a receipt. Six is the shape of a
// conversation; more is a mailbox, and this blob is persisted per receipt for
// up to fifty leads.
const STUB_HISTORY_MAX = 6

const STUB_PERSONALIZATION_FIELDS = [
  ['service_area', 90],
  ['commercial_residential', 30],
  ['annual_volume', 30],
  ['trade', 50],
  ['city_state', 50],
  ['enriched_source', 50],
  ['contact_name', 40],
]

// `key` is keyOf() — the same key the tables, lists and the run's own tracer
// use, so the flow view looks a lead up with the key it already holds.
//
// A follow-up run additionally computed WHERE each lead sits in the cadence —
// the next touch, when it was last touched, how long ago in business days, and
// how many prior touches. Those ride along when present: the card needs the
// touch number to draft the right email, and the provenance story needs the
// dates. Absent on initial-outreach stubs and on every receipt from before
// follow-ups existed, so every reader must guard.
// `canResolveResearch` is the receipt's promise that it can finish the job: a
// pending lead is only left undrafted when the workspace cache is actually
// reachable by key (see packRunMessage — it needs the research category). With
// no way to resolve, a pending lead is drafted from its row exactly as an
// unresearched one always was: an undrafted card nobody can ever fill is worse
// than an honest row-drafted email.
function leadStub(row, canResolveResearch = false) {
  const email = String(row?.email || '')
  const name = String(row?.name || '').trim()
  const stub = { key: String(keyOf(row || {}) || ''), name: name || email || 'Unnamed lead', email }
  // The website rides along for ONE reason: the HubSpot match. Domain is the
  // strongest tier (the directory matches on it first), and a stub without it
  // falls to the email tier — where a contact attached to two companies in the
  // CRM reads as ambiguous and the card loses its HubSpot link even though the
  // domain would have matched cleanly.
  const website = String(row?.website || row?.domain || '').trim()
  if (website) stub.website = website.slice(0, 200)
  // The bounded personalization fields — see STUB_PERSONALIZATION_FIELDS.
  for (const [field, cap] of STUB_PERSONALIZATION_FIELDS) {
    const value = String(row?.[field] ?? '').trim()
    if (value) stub[field] = value.slice(0, cap)
  }
  // WHO THE EMAIL SAYS HELLO TO, worked out HERE — the one moment the full row
  // and its packed people are in hand (the stub keeps only contact_name, and
  // without the people the resolver could not see that j.kly@ is Jacob
  // Klyachenko's address, so the email opened "Hi there,"). lib/greeting.js
  // holds the rule; the draft payload, the template and the card's recipient
  // label all read this back instead of guessing again from the stub.
  const greeting = greetingFor({
    email,
    contactName: row?.contact_name,
    people: parsePackedContacts(row?.contacts),
    company: name,
    website,
  })
  stub.greeting = {
    name: greeting.name.slice(0, 40),
    ...(greeting.person ? { person: greeting.person.slice(0, 80) } : {}),
    how: greeting.how,
  }
  if (Number.isFinite(row?.touch)) stub.touch = row.touch
  if (row?.lastTouchAt) stub.lastTouchAt = String(row.lastTouchAt)
  if (Number.isFinite(row?.daysSince)) stub.daysSince = row.daysSince
  if (Number.isFinite(row?.touchCount)) stub.touchCount = row.touchCount
  // WHAT THE LAST EMAIL ACTUALLY SAID. The follow-up history now comes from
  // the HubSpot mirror (the system of record), so a due card can show the real
  // subject and opening line of the message this touch follows — read from
  // hs_email_subject / hs_email_text, never reconstructed. Absent when the
  // mirror held no text, and absent on every receipt from before this existed.
  const lastSubject = String(row?.lastSubject || '').trim()
  if (lastSubject) stub.lastSubject = lastSubject.slice(0, 120)
  const lastSnippet = String(row?.lastSnippet || '').trim()
  if (lastSnippet) stub.lastSnippet = lastSnippet.slice(0, 160)
  // THE THREAD ITSELF, dated and bounded. A touch number is an assertion; the
  // messages under it are the evidence, and a rep who can see "we wrote on
  // Jul 8, they answered Jul 9, we wrote again Jul 14" never has to wonder
  // whether the ladder was invented. Every field is copied from a mirrored
  // HubSpot engagement — nothing here is written by a model. Bounded hard
  // because this blob is persisted with the conversation.
  if (Array.isArray(row?.history) && row.history.length) {
    stub.history = row.history.slice(0, STUB_HISTORY_MAX).map((m) => ({
      dir: m?.dir === 'in' ? 'in' : 'out',
      at: String(m?.at || ''),
      ...(m?.subject ? { subject: String(m.subject).slice(0, 120) } : {}),
      ...(m?.snippet ? { snippet: String(m.snippet).slice(0, 200) } : {}),
      ...(m?.from ? { from: String(m.from).slice(0, 120) } : {}),
      ...(m?.bounced ? { bounced: true } : {}),
      ...(m?.auto ? { auto: true } : {}),
    })).filter((m) => m.at)
  }
  // WHO "WE" ARE, for the draft route's refusal gate. The run derives this from
  // the workspace itself (agentRun.js#matchCohort, off readOwnOrg plus the
  // mirror's own weighing of which domains send more than they receive), and the
  // lambda's gate otherwise knows only its SELLER_DOMAINS constant. Carried on
  // the stub so Chat.jsx#draftPayloadOf can send it, and bounded here because
  // this blob is persisted with the conversation — the route reads at most
  // twelve, and a domain list longer than that is not a domain list.
  if (Array.isArray(row?.ourDomains) && row.ourDomains.length) {
    const domains = row.ourDomains
      .map((d) => String(d || '').trim().toLowerCase().replace(/^@/, '').slice(0, 120))
      .filter(Boolean)
      .slice(0, 8)
    if (domains.length) stub.ourDomains = domains
  }
  // Undelivered sends are NEVER touches; the count rides along so the card can
  // disclose them rather than leaving a silent gap in the ladder.
  if (Number.isFinite(row?.bouncedCount) && row.bouncedCount > 0) {
    stub.bouncedCount = row.bouncedCount
  }
  // The web-research provenance, bounded (see the research merge in
  // agentRun.js). `researched` on the STUB is the mode string — 'fresh' or
  // 'cached' — where the live row carries researched:true + researchMode;
  // outreachDraft.js#readResearch reads both shapes. research_sources
  // collapses from an array of domains to one joined string here, because a
  // receipt needs the citation line, not the structure. Absent on unresearched
  // rows and on every receipt from before research existed, so those render
  // exactly as before — no researched claim without a dossier behind it.
  Object.assign(stub, packResearch(row))
  // The run's admissions — a lane it could not read (inbox, outcome log) — ride
  // on the stub so the provenance story can repeat the admission instead of
  // claiming the check passed.
  if (row?.replyUnchecked) stub.replyUnchecked = true
  if (row?.exclUnchecked) stub.exclUnchecked = true
  // RESEARCH STILL ON ITS WAY. The engine hands over a lead whose dossier the
  // pipeline is genuinely still producing (agentRun.js's 'pending' verdict).
  // Such a lead is NOT drafted here: an email written now would be written from
  // the import row, frozen onto the receipt, and still be sitting there
  // claiming "nothing was browsed" when the dossier lands two minutes later.
  // The stub says what it is waiting for; the receipt's own resolver finishes
  // the job with a free cache read and writes the draft from the research.
  // WRITTEN ON THE SERVER (lib/operationRuns.js). A run that ran as its Step
  // Functions machine hands the lead over with its email already in the slot —
  // written by the machine's write-email passes under the same rules as POST
  // /enrich/draft, or declined with the writer's reason. It is kept exactly as
  // it came, write-once like every other draft slot; only a lead the machine
  // left `draftPending` falls through to the resolver below.
  if (row?.draft && typeof row.draft === 'object' && (row.draft.bodyText || row.draft.skipped)) {
    stub.draft = row.draft
    return stub
  }
  if (row?.researchPending && canResolveResearch) {
    stub.researchPending = true
    return stub
  }
  // THE MODEL WRITES THE EMAIL, NOT THIS FUNCTION. A receipt that can reach the
  // API (it has a research category — the same condition that lets it finish a
  // pending dossier) leaves the draft slot EMPTY and marked `draftPending`: the
  // receipt's own resolver (Chat.jsx#resolveLeadDraft) asks POST /enrich/draft
  // to write it from the row + the full cached dossier, and stores what came
  // back — text, cited facts and the exec record naming the code that ran.
  // The template below is now the FALLBACK, taken only when that call fails,
  // and it says so in its exec. A receipt that cannot reach the API drafts from
  // the template immediately, exactly as before: an empty slot nobody can fill
  // is worse than an honest template.
  if (canResolveResearch) {
    stub.draftPending = true
    return stub
  }
  return withDraft(stub, row)
}

/**
 * The research fields a stub carries, capped for storage. Extracted from
 * leadStub because TWO writers need identical caps: the run that packs the
 * receipt, and the client resolver that fills in a lead whose dossier landed
 * afterwards. Different caps in the two paths would mean the same lead's
 * citations read differently depending on when its research came back.
 *
 * Returns {} for an unresearched row — absence is how every reader already
 * spells "no dossier".
 */
export function packResearch(row) {
  const out = {}
  const researchMode = row?.researchMode === 'fresh' || row?.researchMode === 'cached' ? row.researchMode : ''
  if (row?.researched && researchMode) {
    out.researched = researchMode
    const researchedAt = String(row?.researchedAt || '').trim()
    if (researchedAt) out.researchedAt = researchedAt.slice(0, 30)
    // Whole domains only, up to the 140 budget. Slicing the JOINED string
    // could cut the last domain mid-token, and every receipt citation (chip,
    // provenance, narrative) would then name a "domain" the dossier never
    // held. A domain that doesn't fit is dropped entire — fewer citations
    // beats an invented one.
    const domains = (
      Array.isArray(row?.research_sources)
        ? row.research_sources
        : String(row?.research_sources || '').split(',')
    )
      .map((d) => String(d || '').trim())
      .filter(Boolean)
    let sources = ''
    for (const domain of domains) {
      const joined = sources ? `${sources}, ${domain}` : domain
      if (joined.length > 140) break
      sources = joined
    }
    if (sources) out.research_sources = sources
    // The SOURCE URLS — the same whole-token discipline, a wider budget, and
    // NO citation strip (a URL is not prose; stripping inside one would make
    // a link that 404s or, worse, points somewhere else). These are what the
    // story renders as clickable evidence, so a half-URL here would be a
    // fabricated citation on screen. A URL that does not fit is dropped
    // entire, and packing stops there rather than reordering the evidence.
    const urls = (
      Array.isArray(row?.research_source_urls)
        ? row.research_source_urls
        : String(row?.research_source_urls || '').split(/[,\s]+/)
    )
      .map((u) => String(u || '').trim())
      .filter(Boolean)
    let packedUrls = ''
    for (const url of urls) {
      const joined = packedUrls ? `${packedUrls}, ${url}` : url
      if (joined.length > 400) break
      packedUrls = joined
    }
    if (packedUrls) out.research_source_urls = packedUrls
    // The worker's exec record, whole — it is small (a few hundred bytes of
    // names and shas) and it is the one thing on the stub that names the code
    // that produced the research. Only ever an object the API returned.
    if (row?.research_exec && typeof row.research_exec === 'object') {
      out.research_exec = row.research_exec
    }
    for (const [field, cap] of [
      ['research_current_solution', 120],
      ['research_recent_activity', 160],
      ['research_service_area', 90],
      ['research_pain_points', 160],
      // The opener is a whole sentence written to be sent as-is, so it gets
      // the widest budget of the set; the buying signal is one dated line.
      ['research_opener', 240],
      ['research_buying_signal', 160],
    ]) {
      // Citations are stripped BEFORE the cap. Slicing raw dossier text could
      // cut an inline "(per domain.com)" in half, and the unbalanced
      // "(per …" fragment would defeat the render-time strip (which needs the
      // closing paren) and ship verbatim in an outbound email body. The
      // trailing-fragment scrub is belt and braces for a parenthetical the
      // dossier text itself left unbalanced, which the cap could still expose.
      const value = stripInlineCitations(row?.[field])
      if (!value) continue
      const capped = value
        .slice(0, cap)
        .replace(/\s*\(\s*(?:per|source:?|via)\s[^()]*$/i, '')
        .trim()
      if (capped) out[field] = capped
    }
  }
  return out
}

/**
 * Write the draft onto a stub, from the row it should be written from.
 *
 * Split out of leadStub for the same reason packResearch was: the receipt is
 * no longer the only writer. A lead whose dossier lands after the run is
 * drafted by this exact function, from a row merged with that dossier, so an
 * email written a minute later is the email the run would have written had the
 * research come back in time.
 *
 * A builder that throws leaves the stub without a draft — the card falls back
 * to building one, exactly as every receipt did before drafts were stored.
 */
export function withDraft(stub, row, fallbackReason = '') {
  // ── the DRAFT ITSELF, saved with the receipt ──────────────────────────────
  //
  // The receipt is a RECORD of what the run produced, so it stores the email,
  // not just the inputs to rebuild one. Three reasons, in order of how much
  // they bite:
  //
  //   1. A rebuilt draft is only as good as the STUB, whose research fields
  //      are capped for storage (120-160 chars). The draft built HERE sees the
  //      full row — the whole dossier value, uncapped — so what Bo reads is
  //      the best version, not a truncated re-derivation.
  //   2. The builders change (they have, repeatedly). A receipt that re-derives
  //      would silently render TODAY's wording for an email Bo copied and sent
  //      LAST WEEK. The record would quietly disagree with the outbox.
  //   3. It renders instantly on reload — no per-card rebuild, and the drafting
  //      beat plays once, when the draft is genuinely being made.
  //
  // bodyHtml is deliberately NOT stored: it is a pure function of bodyText
  // (htmlFromText), so storing it would double the cost of the one field that
  // dominates the size. The card rebuilds it at copy time.
  try {
    const touch = Number(stub.touch)
    const followMeta =
      Number.isFinite(touch) && touch >= 2 && isEmailTouch(touch)
        ? {
            touch,
            daysSince: Number.isFinite(row?.daysSince) ? row.daysSince : null,
            lastTouchAt: String(row?.lastTouchAt || ''),
            touchCount: Number.isFinite(row?.touchCount) ? row.touchCount : null,
          }
        : null
    const built = humanizeDraft(
      followMeta ? buildFollowupEmail(row || {}, followMeta) : buildOutreachEmail(row || {}),
    )
    if (built?.bodyText) {
      stub.draft = {
        subject: built.subject,
        bodyText: built.bodyText,
        ...(built.report ? { report: built.report } : {}),
        ...(Array.isArray(built.sources) && built.sources.length
          ? { sources: built.sources }
          : {}),
        // WHAT WROTE THIS: the browser template in outreachDraft.js. When it
        // stood in for the model, `fallback` carries the reason — the
        // Developer view shows both, so nobody mistakes a template for a
        // model draft or wonders why the model did not write it.
        exec: templateExec(followMeta ? 'followup' : 'initial', fallbackReason),
      }
    }
  } catch {
    // A builder that throws (a follow-up touch that refuses to draft, say)
    // leaves the stub without a saved draft — the card falls back to building
    // one, exactly as every receipt did before this existed.
  }

  return stub
}

// One call-prep item, trimmed to what the call card renders. `points` are the
// talking points the run built (buildCallPoints) — short strings, kept as
// given, capped at the contract's five. '' for a phone genuinely unknown; the
// card renders that as the honest "no number on the row" line, never a guess.
function callStub(item) {
  const email = String(item?.email || '')
  const name = String(item?.name || '').trim()
  const out = {
    key: String(item?.key || ''),
    name: name || email || 'Unnamed lead',
    email,
    phone: String(item?.phone || ''),
    lastTouchAt: item?.lastTouchAt ? String(item.lastTouchAt) : '',
    points: Array.isArray(item?.points)
      ? item.points
          .slice(0, 5)
          .map((p) => String(p || '').trim())
          .filter(Boolean)
      : [],
  }
  if (Number.isFinite(item?.touch)) out.touch = item.touch
  if (Number.isFinite(item?.daysSince)) out.daysSince = item.daysSince
  // The dial sheet gets the same real last-email evidence as an email card —
  // Bo reads the thread before he rings it.
  const callSubject = String(item?.lastSubject || '').trim()
  if (callSubject) out.lastSubject = callSubject.slice(0, 120)
  const callSnippet = String(item?.lastSnippet || '').trim()
  if (callSnippet) out.lastSnippet = callSnippet.slice(0, 160)
  if (Number.isFinite(item?.bouncedCount) && item.bouncedCount > 0) {
    out.bouncedCount = item.bouncedCount
  }
  // Same admissions as leadStub — the dial sheet's provenance must be able to
  // say "the inbox could not be read" when that is what happened.
  if (item?.replyUnchecked) out.replyUnchecked = true
  if (item?.exclUnchecked) out.exclUnchecked = true
  return out
}

// ONE OF BO'S OWN TASKS, as he typed it. The subject is his wording and is
// never rewritten — the whole value of this lane is that it is his sentence,
// not the app's summary of it.
function taskStub(t) {
  const out = {
    id: String(t?.id || ''),
    subject: String(t?.subject || '').trim().slice(0, 160),
    dueAt: String(t?.dueAt || ''),
    overdue: Boolean(t?.overdue),
  }
  // PACK THE STATUS. It is the pill on each row, in HubSpot's own wording.
  // (The chip FILTER that also read it was removed 2026-09-02: with the window
  // at overdue-or-due-today every row is NOT_STARTED, so the chips were two
  // buttons over one list.) This stub used to drop the field entirely, so every
  // packed task arrived status:undefined and the pill vanished. Same for
  // dueToday: it is what lets a row say "due today" rather than nothing at all.
  // And the rep's name, so a multi-rep roster can label whose task each row is.
  const status = String(t?.status || '').trim()
  if (status) out.status = status.slice(0, 40)
  if (t?.dueToday) out.dueToday = true
  const rep = String(t?.rep || '').trim()
  if (rep) out.rep = rep.slice(0, 60)
  const company = String(t?.company || '').trim()
  if (company) out.company = company.slice(0, 80)
  const companyId = String(t?.companyId || '').trim()
  if (companyId) out.companyId = companyId
  // The receipt's row links straight to the task in HubSpot — pack the link
  // or the row renders linkless and the investor-grade "check it yourself"
  // affordance silently disappears.
  const url = String(t?.hubspotUrl || '').trim()
  if (url) out.hubspotUrl = url.slice(0, 300)
  // THE EMAIL BEHIND THE TASK (2026-10-09): a task from the recut run carries
  // the person the email goes to and the facts it is drafted from
  // (agentRun.js#judgeTaskEmails), and resolves on the receipt exactly like a
  // customer item — key, kind, draftPending, and the draft once it lands. A
  // task from an older run carries none of this and packs as it always did.
  if (t?.kind === 'task_followup') {
    out.kind = 'task_followup'
    out.key = String(t?.key || `task:${out.id}`).slice(0, 80)
    const str = (field, cap) => {
      const v = String(t?.[field] || '').trim()
      if (v) out[field] = v.slice(0, cap)
    }
    str('recipient', 160)
    str('email', 160)
    str('name', 120)
    str('contactRole', 80)
    str('contactUrl', 300)
    str('noRecipientReason', 160)
    str('lastOutboundAt', 40)
    str('lastInboundAt', 40)
    str('lastSubject', 120)
    str('lastSnippet', 160)
    str('dealName', 80)
    str('lastSoftwareAccess', 40)
    str('trialEnds', 40)
    str('subscriptionEnd', 40)
    if (Number.isFinite(t?.overdueDays)) out.overdueDays = t.overdueDays
    if (typeof t?.paymentMethodAttached === 'boolean') out.paymentMethodAttached = t.paymentMethodAttached
    if (t?.draftPending) out.draftPending = true
    // A draft already on the item (a receipt re-packed by the host) is kept
    // as it is — write-once, same as every other draft slot.
    if (t?.draft && typeof t.draft === 'object') out.draft = t.draft
    if (Array.isArray(t?.history) && t.history.length) {
      out.history = t.history.slice(0, STUB_HISTORY_MAX).map((m) => ({
        dir: m?.dir === 'in' ? 'in' : 'out',
        at: String(m?.at || ''),
        ...(m?.subject ? { subject: String(m.subject).slice(0, 120) } : {}),
        ...(m?.snippet ? { snippet: String(m.snippet).slice(0, 200) } : {}),
        ...(m?.from ? { from: String(m.from).slice(0, 120) } : {}),
        ...(m?.bounced ? { bounced: true } : {}),
        ...(m?.auto ? { auto: true } : {}),
      })).filter((m) => m.at)
    }
    if (Array.isArray(t?.notes) && t.notes.length) {
      out.notes = t.notes.slice(0, 2).map((n) => ({
        ...(n?.at ? { at: String(n.at).slice(0, 40) } : {}),
        text: String(n?.text || '').slice(0, 320),
      })).filter((n) => n.text)
    }
    if (Array.isArray(t?.ourDomains) && t.ourDomains.length) {
      const domains = t.ourDomains
        .map((d) => String(d || '').trim().toLowerCase().replace(/^@/, '').slice(0, 120))
        .filter(Boolean)
        .slice(0, 12)
      if (domains.length) out.ourDomains = domains
    }
  }
  return out
}

/**
 * Choose WHICH tasks a capped receipt carries.
 *
 * THE BUG THIS EXISTS TO PREVENT. The lane sorts oldest-due first, and every
 * overdue row is by definition earlier than every row due today — so the sort
 * is a hard partition: all the overdue ones, then today's. A plain
 * `slice(0, limit)` therefore took `limit` OVERDUE rows and never reached
 * today's at all. With Bo's live day (38 overdue, 15 due today) and the old cap
 * of 12, the "what do I do today" briefing could not show a single task due
 * today. It was not a rare edge: it was every single run.
 *
 * So the cap is spent on today's rows FIRST — they are the ones the day is
 * named after and the only ones that can still be done on time — and whatever
 * is left goes to the oldest overdue rows. Due order is then restored, so the
 * receipt still reads oldest-first. The true total travels separately as
 * taskCount, so the section says "…and N more in HubSpot" either way.
 */
function packTasks(rows, limit) {
  const all = Array.isArray(rows) ? rows : []
  if (all.length <= limit) return all
  const today = all.filter((t) => t?.dueToday)
  const overdue = all.filter((t) => !t?.dueToday)
  const kept = [...today.slice(0, limit), ...overdue.slice(0, Math.max(0, limit - today.length))]
  const order = new Map(all.map((t, i) => [t, i]))
  return kept.sort((a, b) => order.get(a) - order.get(b))
}

// A lead inside the wait window: nothing to do yet, and WHEN there will be.
function waitingStub(item) {
  const out = {
    key: String(item?.key || ''),
    name: String(item?.name || '').trim() || 'Unnamed lead',
    lastTouchAt: item?.lastTouchAt ? String(item.lastTouchAt) : '',
    dueAt: item?.dueAt ? String(item.dueAt) : '',
  }
  if (Number.isFinite(item?.touch)) out.touch = item.touch
  return out
}

// A lead who replied — pulled out of the cadence for a human to answer.
//
// WHAT THEY SAID rides along. This lane exists so Bo can answer a real person,
// and a name with a date does not tell him whether the reply was "send pricing
// for 4 seats" or "remove me". The snippet is read from the mirror's
// hs_email_text with the quoted block already stripped upstream, so our own
// words can never be shown back as theirs. `via` names the source that saw the
// reply (HubSpot email engagements, or the connected inbox) — a citation, not
// decoration. Every field is omitted when the source held no text.
function replyStub(item) {
  const email = String(item?.email || '')
  const name = String(item?.name || '').trim()
  const out = {
    key: String(item?.key || ''),
    name: name || email || 'Unnamed lead',
    email,
    lastInboundAt: item?.lastInboundAt ? String(item.lastInboundAt) : '',
  }
  const via = String(item?.via || '').trim()
  if (via) out.via = via.slice(0, 60)
  const replySubject = String(item?.replySubject || '').trim()
  if (replySubject) out.replySubject = replySubject.slice(0, 120)
  const replySnippet = String(item?.replySnippet || '').trim()
  if (replySnippet) out.replySnippet = replySnippet.slice(0, 200)
  const replyAt = String(item?.replyAt || '').trim()
  if (replyAt) out.replyAt = replyAt
  return out
}

// WHAT A RUN LEAVES BEHIND: nothing outside its own receipt. An operation
// reads the directory and the HubSpot mirror, researches companies into the
// workspace cache, and writes its drafts onto the receipt itself — there is no
// workspace object for a stopped run to have half-made. Both narrators say it
// in exactly these words: runHost.js puts it on the node that took the blame,
// this packer puts it under a nothing-was-due headline, and the dedupe below
// means a receipt carrying both still reads it once.
export const NOTHING_LEFT_BEHIND =
  'Nothing was left behind — a run only reads and drafts, and the drafts live on its receipt.'

/**
 * Build the message an operation posts into the thread when it finishes.
 *
 * @param {object}   run
 * @param {string}   run.operationId     which operation ran
 * @param {string}   run.operationLabel  its label, stored so a receipt still
 *                                       names itself if the catalog changes
 * @param {object[]} run.passes          the run's nodes, as it left them
 * @param {object}   [run.result]        runOperation's return value, or null
 * @returns a {role:'run'} message: text for the thread, plus the payload the
 *   provenance view reopens from.
 */
// A written-off admission (tasksDismissed / customersDismissed) bounded for
// storage: the counts, at most `limit` companies, each with its reason and,
// for a note, the rep's own words trimmed.
function packWriteOffs(value, limit) {
  const v = value && typeof value === 'object' ? value : {}
  return {
    count: Number(v.count) || 0,
    ...(Number(v.closedLost) > 0 ? { closedLost: Number(v.closedLost) } : {}),
    ...(Number(v.notAFit) > 0 ? { notAFit: Number(v.notAFit) } : {}),
    ...(v.unapplied === true ? { unapplied: true } : {}),
    companies: (Array.isArray(v.companies) ? v.companies : [])
      .slice(0, limit)
      .map((c) => ({
        name: String(c?.name || '').trim().slice(0, 80),
        reason: c?.reason === 'closed_lost' ? 'closed_lost' : 'not_a_fit',
        ...(c?.at ? { at: String(c.at).slice(0, 40) } : {}),
        ...(c?.quote ? { quote: String(c.quote).slice(0, 160) } : {}),
      }))
      .filter((c) => c.name),
  }
}

export function packRunMessage({
  operationId,
  operationLabel = '',
  passes = [],
  result = null,
  at = Date.now(),
}) {
  const nodes = passes.map(packPass)
  const nothingToDo = result?.nothingToDo === true
  const built = Boolean(result) && result.ok !== false && !nothingToDo
  const failed = passes.find((p) => p.status === 'failed') || null
  // The last node that actually ran — never an abandoned one, which by
  // definition never ran and has nothing to say.
  const last = [...passes].reverse().find((p) => p.status !== 'pending' && !p.abandoned) || null

  // THE CATEGORY THE RESEARCH RAN UNDER, once per receipt rather than once per
  // stub — the workspace cache is keyed by (category, lead key), and sixty
  // copies of the same string is pure waste against STORE_BUDGET.
  //
  // It is also the receipt's ABILITY to finish a pending lead, which is why it
  // is computed before the stubs: with no category there is no cache read, so
  // no lead may be left waiting, so every lead is drafted from its row exactly
  // as it always was.
  const researchCategory = String(result?.researchCategory || '').trim().slice(0, 60)
  const stubs = (Array.isArray(result?.selected) ? result.selected : []).map((row) =>
    leadStub(row, Boolean(researchCategory)),
  )
  const reachable = new Set(stubs.map((s) => s.key).filter(Boolean))
  // Leads whose draft slot is still open — waiting on research, or on the
  // model to write it. Either way the receipt keeps its category so the
  // resolver can finish the job.
  const researchPendingCount = stubs.filter((s) => s.researchPending || s.draftPending).length

  const raw = result?.trace && typeof result.trace === 'object' ? result.trace : null
  let trace = null
  let traceDropped = 0
  if (raw) {
    trace = {}
    for (const [leadKey, steps] of Object.entries(raw)) {
      if (reachable.has(leadKey)) trace[leadKey] = steps
      else traceDropped += 1
    }
    if (!Object.keys(trace).length) trace = null
  }
  const traceOmitted = (Number(result?.traceOmitted) || 0) + traceDropped

  // ── the follow-up lanes ─────────────────────────────────────────────────
  // Present only when the operation computed them; empty arrays otherwise, so
  // a receipt from before follow-ups existed and one from a run with nothing
  // in a lane read the same way. `inboxUnread` is the honest sibling of an
  // empty manualReply: the lane could not be READ, which is not "nobody
  // replied", and the UI says so.
  const worklist = result?.worklist === true
  // WHICH RECEIPT SHAPE (2026-09-23 recut): 'tasks' is Daily Tasks — the
  // rep's HubSpot tasks and nothing else; 'customers' is Follow-Up — the four
  // customer lanes and no task section. A result without it is an older
  // shape and packs exactly as it always did, so every stored receipt keeps
  // its own layout.
  const briefing =
    result?.briefing === 'tasks' || result?.briefing === 'customers' ? result.briefing : ''
  const inboxUnread = result?.inboxUnread === true
  const tasksAll = (Array.isArray(result?.tasks) ? result.tasks : []).map(taskStub).filter((t) => t.subject || t.company)
  const callsAll = (Array.isArray(result?.calls) ? result.calls : []).map(callStub)
  const waitingAll = (Array.isArray(result?.waiting) ? result.waiting : []).map(waitingStub)
  const repliesAll = (Array.isArray(result?.manualReply) ? result.manualReply : []).map(replyStub)
  // Bo's customer lanes. The lane items arrive already stubbed and bounded
  // (agentRun caps each lane at 30 and builds compact items), so the caps here
  // are belt-and-braces.
  const inactiveAll = Array.isArray(result?.inactiveUsers) ? result.inactiveUsers : []
  const expiredAll = Array.isArray(result?.expiredSubscriptions) ? result.expiredSubscriptions : []
  // Section 4 (2026-09-03): who is on trial right now. Facts only — no draft
  // rides on these, so there is nothing for the resolver to fill later.
  const trialAll = Array.isArray(result?.trialUsers) ? result.trialUsers : []
  // Follow-Up's fourth lane (2026-09-23): facts only, no draft. Packed only on
  // a 'customers' receipt, so every older shape stays byte-identical.
  const paymentAll = Array.isArray(result?.paymentFailed) ? result.paymentFailed : []
  const paymentCount = Number(result?.paymentFailedCount) || paymentAll.length
  const callCount = callsAll.length
  const waitingCount = waitingAll.length
  const manualReplyCount = repliesAll.length
  const inactiveCount = Number(result?.inactiveUserCount) || inactiveAll.length
  const expiredCount = Number(result?.expiredSubscriptionCount) || expiredAll.length
  const trialCount = Number(result?.trialUserCount) || trialAll.length

  const count = stubs.length
  const leads = count === 1 ? 'lead' : 'leads'
  const inboxLine =
    'The inbox could not be read, so replies are unknown — connect Gmail to see them.'
  let outcome = 'stopped'
  let title = ''
  let lines = []

  if (built && briefing === 'tasks') {
    // DAILY TASKS: the rep's HubSpot tasks. The title is the count, split
    // the way the rep reads it — overdue first, then today's.
    outcome = 'built'
    const n = tasksAll.length
    const overdue = tasksAll.filter((t) => t.overdue).length
    const today = n - overdue
    const whose = Array.isArray(result?.tasksFor) && result.tasksFor.length ? `${result.tasksFor.join(' and ')}’s` : 'The rep’s'
    if (result?.tasksUnread) {
      title = 'Daily Tasks — the HubSpot tasks could not be read'
    } else if (!n) {
      title = `Daily Tasks — ${whose === 'The rep’s' ? 'no task' : `${whose.replace(/’s$/, '')} has no task`} overdue or due today`
    } else {
      const bits = []
      if (overdue) bits.push(`${overdue} overdue`)
      if (today) bits.push(`${today} due today`)
      const drafted = tasksAll.filter((t) => t.recipient).length
      if (drafted) bits.push(`${drafted} ${drafted === 1 ? 'email' : 'emails'}`)
      title = `Daily Tasks — ${n} ${n === 1 ? 'task' : 'tasks'} · ${bits.join(' · ')}`
    }
    lines = [
      tasksAll.some((t) => t.kind === 'task_followup')
        ? 'One email per task, written from the task, the real thread with that company and the rep’s notes. Nothing was created and nothing sends on its own — copy it into HubSpot, or send it through Apollo from the card.'
        : 'The rep’s own HubSpot tasks, in their own wording, each linked to its record. Nothing was created and nothing sends.',
    ]
  } else if (built && briefing === 'customers') {
    // FOLLOW-UP: the four customer lanes. Only non-zero segments are named,
    // and an unread lane is named as unread — "0 payment failed" over a
    // property the portal never wrote would be a false zero.
    outcome = 'built'
    const segments = []
    if (result?.usersUnread) {
      segments.push('customer usage unread')
    } else {
      if (inactiveCount) segments.push(`${inactiveCount} inactive`)
      if (trialCount) segments.push(`${trialCount} ending soon`)
      if (expiredCount) segments.push(`${expiredCount} expired`)
      if (result?.paymentFailedUnread) segments.push('payment failed unread')
      else if (paymentCount) segments.push(`${paymentCount} payment failed`)
    }
    title = segments.length ? `Follow-Up — ${segments.join(' · ')}` : 'Follow-Up — nobody to follow up today'
    lines = ['A follow-up briefing: every check-in and its draft is on this receipt, and nothing sends unless you press Send via Apollo.']
  } else if (built && worklist) {
    // The morning briefing. It is a READ of the day — HubSpot tasks, customers,
    // trials — so the title is the counts and the first line says where the
    // work it found actually lives. Only non-zero segments are named: "0 calls"
    // is noise, not honesty.
    outcome = 'built'
    const segments = []
    if (count) segments.push(`${count} due`)
    if (callCount) segments.push(`${callCount} ${callCount === 1 ? 'call' : 'calls'}`)
    if (waitingCount) segments.push(`${waitingCount} waiting`)
    if (inactiveCount) segments.push(`${inactiveCount} inactive`)
    if (expiredCount) segments.push(`${expiredCount} expired`)
    if (trialCount) segments.push(`${trialCount} on trial`)
    title = segments.length ? `Bo’s worklist — ${segments.join(' · ')}` : 'Bo’s worklist'
    lines = [
      'A briefing: everything it found — the drafts included — is on this receipt, and nothing sends.',
      ...(inboxUnread ? [inboxLine] : []),
    ]
  } else if (built) {
    outcome = 'built'
    // The headline of the whole feature: "50 leads selected". A run writes
    // nothing outside this receipt, so the count IS the headline and the leads
    // it counted are the cards underneath it.
    title =
      count === 0 && callCount
        ? `${callCount} call ${callCount === 1 ? 'touch' : 'touches'} due — no emails to draft`
        : `${count} ${leads} selected`
    // No confirmation lines by design: the title already says what the run
    // found, and the standing "nothing sends" footer carries the draft
    // reassurance. Only genuinely informative lines remain.
    lines = [
      callCount
        ? `${callCount} call ${callCount === 1 ? 'touch is' : 'touches are'} due — talking points are on this receipt. Bo dials; the app never does.`
        : '',
      inboxUnread ? inboxLine : '',
    ].filter(Boolean)
  } else if (nothingToDo) {
    outcome = 'nothing'
    // A no-op is a real outcome with a real headline, and the node that ended
    // the run wrote it. Never invented here.
    title = last?.headline || 'Nothing was due today'
    // The node that ended the run usually already says nothing was left
    // behind — the reassurance is only added when it did not, so the receipt
    // never says the same sentence twice (dedupe below catches it either way).
    lines = [...(last?.detail || []).slice(0, 2), NOTHING_LEFT_BEHIND]
  } else {
    outcome = 'stopped'
    title = failed?.headline || last?.headline || 'The run stopped before it built anything'
    lines = (failed?.detail || last?.detail || []).slice(0, 2)
  }

  return {
    role: 'run',
    at,
    operationId,
    operationLabel,
    outcome,
    title,
    // A node's own detail and the receipt's standing reassurance are often the
    // same sentence; saying it twice reads as a stutter, not as emphasis.
    lines: [...new Set(lines.filter(Boolean))],
    leads: stubs.slice(0, RUN_LEAD_LIMIT),
    leadCount: count,
    // WHERE THE CARDS CAN BE READ BACK FROM (2026-10-12). A receipt built on
    // the server names its run, so a copy the browser shed to fit its storage
    // (shedRun) is refilled from the run's own rows - which the server keeps
    // for thirty days - instead of rendering empty over the work it did.
    ...(result?.serverRun?.runId
      ? {
          serverRun: {
            runId: String(result.serverRun.runId).slice(0, 80),
            machine: String(result.serverRun.machine || '').slice(0, 40),
            finishedAt: String(result.serverRun.finishedAt || '').slice(0, 40),
          },
        }
      : {}),
    // Present ONLY on a receipt that is genuinely waiting for research. Its
    // absence is what makes every older receipt inert: no category, no
    // resolver, no lookups — they render exactly as they always have.
    ...(researchPendingCount ? { researchCategory } : {}),
    // The follow-up lanes, capped like the leads, with true counts kept so the
    // receipt can say "N more" instead of silently showing fewer.
    // Bo's own tasks, first lane of the daily pass. Bounded like every other
    // lane; the true count rides along so the receipt can say "N more".
    tasks: packTasks(tasksAll, RUN_TASK_LIMIT),
    taskCount: tasksAll.length,
    ...(result?.tasksUnread ? { tasksUnread: true } : {}),
    // Whose tasks section 1 is — the roster reps the lane resolved — so the
    // receipt can say "Bo Garcia has no task overdue" and never "you".
    ...(Array.isArray(result?.tasksFor) && result.tasksFor.length
      ? { tasksFor: result.tasksFor.map((n) => String(n || '').trim().slice(0, 60)).filter(Boolean).slice(0, 8) }
      : {}),
    // And whose could NOT be read, so a partial answer never renders as a
    // complete short one.
    ...(Array.isArray(result?.tasksUnreadFor) && result.tasksUnreadFor.length
      ? { tasksUnreadFor: result.tasksUnreadFor.map((n) => String(n || '').trim().slice(0, 60)).filter(Boolean).slice(0, 8) }
      : {}),
    // And how many were LEFT OFF as our own staff / test accounts (the
    // Settings exclusion list, lib/taskExclusions.js), with the companies and
    // the entries in force — so the receipt can say "3 hidden" under the list,
    // and under the EMPTY state too. This field has to cross the packer like
    // the two above it: the node put it on the result from day one, and the
    // review that caught this found the receipt printing "no task overdue"
    // over three hidden ones because nothing carried the count this far.
    // Bounded like every other field here; carried only when it says
    // something, so every older receipt is byte-identical.
    // Carried when something was hidden — OR when the list could not be
    // applied at all (`unapplied`: the mirror sent no company domain), which
    // the receipt must say rather than let "0 hidden" stand in for "not
    // checked".
    ...(Number(result?.tasksExcluded?.count) > 0 || result?.tasksExcluded?.unapplied === true
      ? {
          tasksExcluded: {
            count: Number(result.tasksExcluded.count) || 0,
            ...(result.tasksExcluded.unapplied === true ? { unapplied: true } : {}),
            companies: (Array.isArray(result.tasksExcluded.companies) ? result.tasksExcluded.companies : [])
              .map((n) => String(n || '').trim().slice(0, 60))
              .filter(Boolean)
              .slice(0, 5),
            entries: (Array.isArray(result.tasksExcluded.entries) ? result.tasksExcluded.entries : [])
              .map((n) => String(n || '').trim().slice(0, 80))
              .filter(Boolean)
              .slice(0, 16),
          },
        }
      : {}),
    calls: callsAll.slice(0, RUN_CALL_LIMIT),
    callCount,
    waiting: waitingAll.slice(0, RUN_WAITING_LIMIT),
    waitingCount,
    manualReply: repliesAll.slice(0, RUN_REPLY_LIMIT),
    manualReplyCount,
    inboxUnread,
    // The customer check-in lanes, with true counts so the receipt can say
    // "N more" instead of silently showing fewer. The unread flag carries the
    // run's own reason string — the section renders the reason, and "unread"
    // is never dressed as "empty".
    inactiveUsers: inactiveAll.slice(0, RUN_CUSTOMER_LIMIT),
    inactiveUserCount: inactiveCount,
    expiredSubscriptions: expiredAll.slice(0, RUN_CUSTOMER_LIMIT),
    expiredSubscriptionCount: expiredCount,
    trialUsers: trialAll.slice(0, RUN_CUSTOMER_LIMIT),
    trialUserCount: trialCount,
    ...(result?.usersUnread ? { usersUnread: String(result.usersUnreadReason || 'the customer usage data could not be read') } : {}),
    // The receipt shape and the fourth lane — 'customers' receipts only, so a
    // receipt from before the recut carries neither key and renders as it did.
    ...(briefing ? { briefing } : {}),
    ...(briefing === 'customers'
      ? {
          paymentFailed: paymentAll.slice(0, RUN_CUSTOMER_LIMIT),
          paymentFailedCount: paymentCount,
          ...(result?.paymentFailedUnread ? { paymentFailedUnread: String(result.paymentFailedUnread).slice(0, 300) } : {}),
          ...(Number(result?.trialLater) > 0 ? { trialLater: Number(result.trialLater) } : {}),
          // Customers left off as written off (Closed Lost / a "not a fit"
          // note — 2026-09-25), and the admission when that check did not run.
          ...(Number(result?.customersDismissed?.count) > 0
            ? { customersDismissed: packWriteOffs(result.customersDismissed, 8) }
            : {}),
          ...(result?.dismissalsUnread ? { dismissalsUnread: String(result.dismissalsUnread).slice(0, 300) } : {}),
        }
      : {}),
    // Daily Tasks' two quiet admissions (2026-09-25): tasks left off because
    // the company was written off after the task was set, and older overdue
    // tasks superseded by a later task the rep already booked. Carried only
    // when they say something, so every older receipt is byte-identical.
    ...(briefing === 'tasks' && (Number(result?.tasksDismissed?.count) > 0 || result?.tasksDismissed?.unapplied === true)
      ? { tasksDismissed: packWriteOffs(result.tasksDismissed, 5) }
      : {}),
    ...(briefing === 'tasks' && Number(result?.tasksSuperseded?.count) > 0
      ? {
          tasksSuperseded: {
            count: Number(result.tasksSuperseded.count) || 0,
            companies: (Array.isArray(result.tasksSuperseded.companies) ? result.tasksSuperseded.companies : [])
              .slice(0, 5)
              .map((c) => ({
                name: String(c?.name || '').trim().slice(0, 80),
                next: /^\d{4}-\d{2}-\d{2}$/.test(String(c?.next || '')) ? String(c.next) : '',
              }))
              .filter((c) => c.name),
          },
        }
      : {}),
    worklist,
    stepCount: nodes.length,
    traceOmitted,
    traceDropped,
    passes: nodes,
    // Kept even when ok:false — an abandoned run explains itself through its
    // nodes, and the view reads ok/nothingToDo to decide what it may claim.
    result: result
      ? {
          ok: result.ok === true,
          nothingToDo,
          // The stub array is NOT stored twice. `leads` above is the copy the
          // receipt renders; the only thing anyone ever read here was the
          // count, so that is all this keeps. (AgentRunView falls back to
          // selected?.length for a LIVE result, whose selected holds full rows.)
          selectedCount: stubs.length,
          trace,
          traceOmitted,
        }
      : null,
  }
}

/** The thread's own title when it has one, else the first user message. */
export function titleFor(conversation) {
  if (conversation.title) return conversation.title
  const first = conversation.messages.find((m) => m.role === 'user')
  if (!first) return 'New chat'
  const line = first.text.replace(/\s+/g, ' ').trim()
  return line.length > 44 ? `${line.slice(0, 44)}…` : line || 'New chat'
}
